import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canDownscaleImageFile, downscaleImageFile } from '../utils/imageUpload';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import SelectOptionsEditor from '../components/SelectOptionsEditor';
import ChatBuilderEditor from '../components/ChatBuilderEditor';
import BotSchedulesPage from './BotSchedulesPage';
import './BotsPage.css';

const DEFAULT_THEME_PRESETS = [
  {
    id: 'custom',
    label: 'Tilpasset',
    description: 'Bestem fargene selv.',
    colors: null,
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'navy-slate',
    label: 'Navy Slate',
    description: 'Dyp marineblå base med dempet teal-aksent.',
    colors: {
      gradientStart: '#0c4a6e',
      gradientEnd: '#164e63',
      color: '#0e7490',
      launcherColor: '#155e75',
      panelBgColor: '#0e4a6e',
      inputBgColor: '#083344',
    },
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'clean-white',
    label: 'Clean White',
    description: 'Lyst, nøytralt tema med tydelig blå aksent.',
    colors: {
      gradientStart: '#ffffff',
      gradientEnd: '#eef4ff',
      color: '#1d4ed8',
      launcherColor: '#e0e7ff',
      panelBgColor: '#f8fafc',
      inputBgColor: '#ffffff',
    },
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'ocean-depth',
    label: 'Ocean Depth',
    description: 'Dype blågrønne flater med skarp lesbarhet.',
    colors: {
      gradientStart: '#073042',
      gradientEnd: '#0f766e',
      color: '#0f5f6f',
      launcherColor: '#155e75',
      panelBgColor: '#0e4a5f',
      inputBgColor: '#083344',
    },
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'slate-pro',
    label: 'Slate Pro',
    description: 'Rolig, profesjonelt tema for B2B og support.',
    colors: {
      gradientStart: '#111827',
      gradientEnd: '#334155',
      color: '#475569',
      launcherColor: '#1f2937',
      panelBgColor: '#253143',
      inputBgColor: '#0f172a',
    },
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'forest',
    label: 'Forest',
    description: 'Friskt grønt tema med varme lyse flater.',
    colors: {
      gradientStart: '#f0fdf4',
      gradientEnd: '#bbf7d0',
      color: '#047857',
      launcherColor: '#86efac',
      panelBgColor: '#dcfce7',
      inputBgColor: '#f7fee7',
    },
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'desert-bloom',
    label: 'Desert Bloom',
    description: 'Varmt og rolig tema med terrakottaaksent.',
    colors: {
      gradientStart: '#fff7ed',
      gradientEnd: '#fed7aa',
      color: '#b45309',
      launcherColor: '#fdba74',
      panelBgColor: '#ffedd5',
      inputBgColor: '#fffaf0',
    },
    backgroundPresetSlug: null,
    isBuiltIn: true,
  },
  {
    id: 'floating-lines',
    label: 'Floating Lines',
    description: 'Mørkt tema med dynamiske linjer og klar aksent.',
    colors: {
      gradientStart: '#030712',
      gradientEnd: '#111827',
      color: '#2563eb',
      launcherColor: '#1d4ed8',
      panelBgColor: '#111827',
      inputBgColor: '#030712',
    },
    backgroundPresetSlug: 'floating-lines',
    backgroundConfig: { component_key: 'floating_lines' },
    isBuiltIn: true,
  },
];

const fieldMap = {
  botTitle: 'display_title',
  botType: 'bot_type',
  botProfileConfig: 'bot_profile_config',
  color: 'theme_color',
  gradientStart: 'theme_gradient_start',
  gradientEnd: 'theme_gradient_end',
  launcherColor: 'launcher_bg_color',
  launcherIconUrl: 'launcher_icon_url',
  launcherIconSize: 'launcher_icon_size',
  panelBgColor: 'panel_bg_color',
  inputBgColor: 'input_bg_color',
  uiConfig: 'ui_config',
  themePreset: 'theme_preset',
  welcomeMessage: 'welcome_message',
  systemInstruction: 'system_instruction',
  behavior: 'behavior_notes',
  notificationEmail: 'notification_email',
  logoUrl: 'logo_url',
  defaultIntegrationId: 'default_mcp_integration_id',
  bookingScheduleId: 'booking_schedule_id',
  proactiveMessage: 'proactive_message',
  proactiveDelaySeconds: 'proactive_delay_seconds',
  menuIntroTitle: 'menu_intro_title',
  menuIntroSubtitle: 'menu_intro_subtitle',
  backgroundPresetId: 'background_preset_id',
  backgroundCustomConfig: 'background_custom_config',
  conversationPolicy: 'conversation_policy',
  websiteUrl: 'website_url',
  websiteScanEnabled: 'website_scan_enabled',
  allowedOrigins: 'allowed_origins',
};

const BOT_TYPE_GENERIC = 'generic';
const DEFAULT_PROACTIVE_MESSAGE = 'Hei! Gi et lite hint om hva du trenger, så viser jeg deg veien videre.';
const DEFAULT_PROACTIVE_COOLDOWN_SECONDS = 300;

const getProactiveOpenChatFromUiConfig = (uiConfig) =>
  uiConfig?.behavior?.proactiveOpenChat === true
  || uiConfig?.behavior?.proactive_open_chat === true;

const getProactiveOpenChatDelayFromUiConfig = (uiConfig) => {
  const raw =
    uiConfig?.behavior?.proactiveOpenChatDelaySeconds
    ?? uiConfig?.behavior?.proactive_open_chat_delay_seconds;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? String(Math.floor(parsed)) : '10';
};

const getProactiveCooldownFromUiConfig = (uiConfig) => {
  const raw =
    uiConfig?.behavior?.proactiveCooldownSeconds
    ?? uiConfig?.behavior?.proactive_cooldown_seconds;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0
    ? String(Math.floor(parsed))
    : String(DEFAULT_PROACTIVE_COOLDOWN_SECONDS);
};

const normalizeDelaySeconds = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.min(86400, Math.floor(parsed));
};

const withProactiveBehaviorUiConfig = ({
  uiConfig,
  proactiveCooldownSeconds,
  proactiveOpenChat,
  proactiveOpenChatDelaySeconds,
}) => ({
  ...(uiConfig && typeof uiConfig === 'object' && !Array.isArray(uiConfig) ? uiConfig : {}),
  behavior: {
    ...(uiConfig?.behavior && typeof uiConfig.behavior === 'object' && !Array.isArray(uiConfig.behavior)
      ? uiConfig.behavior
      : {}),
    proactiveCooldownSeconds: normalizeDelaySeconds(proactiveCooldownSeconds),
    proactiveOpenChat: proactiveOpenChat === true,
    proactiveOpenChatDelaySeconds: normalizeDelaySeconds(proactiveOpenChatDelaySeconds),
  },
});

const normalizeBotTypeValue = () => BOT_TYPE_GENERIC;

const normalizeBotProfileConfigValue = () => ({});

const DEFAULT_CONVERSATION_POLICY = Object.freeze({
  humanRequestPreference: 'live_then_queue_then_inquiry',
  salesReadyPreference: 'inquiry_first',
  outsideHoursPreference: 'queue_if_allowed_else_inquiry',
  complaintPreference: 'live_first',
  fallbackInquiryTypeId: null,
});

const HUMAN_REQUEST_OPTIONS = [
  { value: 'live_then_queue_then_inquiry', label: 'Live først, deretter kø eller henvendelse' },
  { value: 'lead_first', label: 'Henvendelse først' },
  { value: 'balanced', label: 'Balansert' },
  { value: 'ai_first', label: 'AI prøver først' },
];

const SALES_READY_OPTIONS = [
  { value: 'inquiry_first', label: 'Samle inn henvendelse først' },
  { value: 'live_first', label: 'Live-agent først' },
  { value: 'balanced', label: 'Balansert' },
  { value: 'ai_first', label: 'AI prøver først' },
];

const OUTSIDE_HOURS_OPTIONS = [
  { value: 'queue_if_allowed_else_inquiry', label: 'Kø hvis tillatt, ellers henvendelse' },
  { value: 'inquiry_first', label: 'Henvendelse først' },
  { value: 'ai_first', label: 'AI svarer videre' },
];

const COMPLAINT_OPTIONS = [
  { value: 'live_first', label: 'Live-agent først' },
  { value: 'balanced', label: 'Balansert' },
  { value: 'lead_first', label: 'Henvendelse først' },
  { value: 'ai_first', label: 'AI prøver først' },
];

const normalizePolicyValue = (value, options, fallback) => (
  options.some((option) => option.value === value) ? value : fallback
);

const normalizeConversationPolicy = (value = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const rawFallbackInquiryTypeId = source.fallbackInquiryTypeId ?? source.fallback_inquiry_type_id ?? null;
  const fallbackInquiryTypeId = rawFallbackInquiryTypeId === null
    || rawFallbackInquiryTypeId === undefined
    || rawFallbackInquiryTypeId === ''
    ? null
    : Number(rawFallbackInquiryTypeId);

  return {
    humanRequestPreference: normalizePolicyValue(
      source.humanRequestPreference,
      HUMAN_REQUEST_OPTIONS,
      DEFAULT_CONVERSATION_POLICY.humanRequestPreference,
    ),
    salesReadyPreference: normalizePolicyValue(
      source.salesReadyPreference,
      SALES_READY_OPTIONS,
      DEFAULT_CONVERSATION_POLICY.salesReadyPreference,
    ),
    outsideHoursPreference: normalizePolicyValue(
      source.outsideHoursPreference,
      OUTSIDE_HOURS_OPTIONS,
      DEFAULT_CONVERSATION_POLICY.outsideHoursPreference,
    ),
    complaintPreference: normalizePolicyValue(
      source.complaintPreference,
      COMPLAINT_OPTIONS,
      DEFAULT_CONVERSATION_POLICY.complaintPreference,
    ),
    fallbackInquiryTypeId: Number.isInteger(fallbackInquiryTypeId) && fallbackInquiryTypeId > 0
      ? fallbackInquiryTypeId
      : null,
  };
};

const ConversationPolicyPanel = ({ policy, onChange, disabled = false }) => {
  const currentPolicy = normalizeConversationPolicy(policy);
  const handleChange = (key, value) => {
    onChange(normalizeConversationPolicy({ ...currentPolicy, [key]: value }));
  };

  return (
    <div className="escalation-policy-panel">
      <h4>Prioritering</h4>
      <p className="panel-description">
        Velg hva boten skal gjøre når flere handlinger er mulige.
      </p>

      <div className="policy-option">
        <label className="policy-field">
          <span className="field-label">Når kunden ber om et menneske</span>
          <span className="field-hint">Styrer om live-chat, kø eller henvendelsesskjema skal vinne.</span>
          <select
            className="input"
            value={currentPolicy.humanRequestPreference}
            onChange={(event) => handleChange('humanRequestPreference', event.target.value)}
            disabled={disabled}
          >
            {HUMAN_REQUEST_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="policy-option">
        <label className="policy-field">
          <span className="field-label">Når kunden virker salgsklar</span>
          <span className="field-hint">Brukes ved kjøp, tilbud, booking eller tydelig kontaktbehov.</span>
          <select
            className="input"
            value={currentPolicy.salesReadyPreference}
            onChange={(event) => handleChange('salesReadyPreference', event.target.value)}
            disabled={disabled}
          >
            {SALES_READY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="policy-option">
        <label className="policy-field">
          <span className="field-label">Utenfor live-tid</span>
          <span className="field-hint">Gjelder når live-agent ikke er direkte tilgjengelig.</span>
          <select
            className="input"
            value={currentPolicy.outsideHoursPreference}
            onChange={(event) => handleChange('outsideHoursPreference', event.target.value)}
            disabled={disabled}
          >
            {OUTSIDE_HOURS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="policy-option">
        <label className="policy-field">
          <span className="field-label">Ved klage, frustrasjon eller hast</span>
          <span className="field-hint">Overstyrer normal prioritering når samtalen bør behandles mer forsiktig.</span>
          <select
            className="input"
            value={currentPolicy.complaintPreference}
            onChange={(event) => handleChange('complaintPreference', event.target.value)}
            disabled={disabled}
          >
            {COMPLAINT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
};

const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const TABS_TOOL_NAME = 'tabs-check-availability';

const normalizeLauncherIconSizeValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 64;
  return Math.max(44, Math.min(96, Math.round(parsed)));
};

const createClientKey = (prefix = 'tmp') => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
const DEFAULT_TABS_FEATURES = Object.freeze({
  courses: true,
  prices: true,
  booking: true,
});
const createTabsLessonDefaultsDraft = (value = null) => {
  const source = value && typeof value === 'object' ? value : {};
  const webCategoryId = source.webCategoryId ?? source.web_category_id;
  const productId = source.productId ?? source.product_id;
  const categoryName = typeof source.categoryName === 'string'
    ? source.categoryName
    : typeof source.category_name === 'string'
      ? source.category_name
      : '';
  const productName = typeof source.productName === 'string'
    ? source.productName
    : typeof source.product_name === 'string'
      ? source.product_name
      : '';
  const className = typeof source.className === 'string'
    ? source.className
    : typeof source.class_name === 'string'
      ? source.class_name
      : '';

  return {
    webCategoryId: webCategoryId !== null && webCategoryId !== undefined ? String(webCategoryId) : '',
    productId: productId !== null && productId !== undefined ? String(productId) : '',
    categoryName: categoryName.trim(),
    productName: productName.trim(),
    className: className.trim(),
  };
};
const createTabsConfigDraft = (seed = {}) => ({
  loaded: true,
  configured: seed.configured === true,
  officeId: seed.officeId !== null && seed.officeId !== undefined ? String(seed.officeId) : '',
  apiKey: '',
  apiKeyMasked: typeof seed.apiKeyMasked === 'string' ? seed.apiKeyMasked : null,
  features: {
    courses: seed.features?.courses !== false,
    prices: seed.features?.prices !== false,
    booking: seed.features?.booking !== false,
  },
  lessonDefaults: createTabsLessonDefaultsDraft(seed.lessonDefaults ?? seed.lesson_defaults ?? null),
  lastTestStatus: seed.lastTestStatus && typeof seed.lastTestStatus === 'object'
    ? seed.lastTestStatus
    : null,
  updatedAt: seed.updatedAt || null,
});

const BOT_SECTIONS = [
  { id: 'overview', label: 'Oversikt' },
  { id: 'chat-builder', label: 'Design' },
  { id: 'behavior', label: 'Adferd' },
  { id: 'schedules', label: 'Tidsskjema' },
  { id: 'tools', label: 'Verktøy' },
  { id: 'knowledge', label: 'Kunnskapsbase' },
  { id: 'security', label: 'Sikkerhet' },
  { id: 'installation', label: 'Installering' },
];

// Bot setup wizard (shown after bot creation).
const BOT_WIZARD_STEPS = [
  {
    id: 'overview',
    label: 'Oversikt',
    sectionId: 'overview',
    description: 'Navn og segment',
  },
  {
    id: 'design',
    label: 'Design',
    sectionId: 'chat-builder',
    description: 'Velg template og se preview',
  },
  {
    id: 'behavior',
    label: 'Adferd',
    sectionId: 'behavior',
    description: 'Dialog, tone og prioritering',
  },
  {
    id: 'tools',
    label: 'Verktøy',
    sectionId: 'tools',
    description: 'Aktiver tilgjengelige verktøy',
  },
  {
    id: 'knowledge',
    label: 'Kunnskapsbase',
    sectionId: 'knowledge',
    description: 'Generer og lagre kunnskap',
  },
  {
    id: 'schedules',
    label: 'Tidsskjema',
    sectionId: 'schedules',
    description: 'Velg chatmodus og åpningstid',
  },
  {
    id: 'security',
    label: 'Sikkerhet',
    sectionId: 'security',
    description: 'Whitelist nettsteder',
  },
  {
    id: 'installation',
    label: 'Installering',
    sectionId: 'installation',
    description: 'Legg til script på nettsiden',
  },
];

const createEmptyKnowledgePair = () => ({
  id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  keyword: '',
  content: '',
  sourceType: 'manual',
  sourceUrl: null,
  sourceUpdatedAt: null,
  readOnly: false,
});

const PRODUCT_SOURCE_LABELS = {
  manual: 'Manuell',
  website_scan: 'Webscan',
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
};

const PRODUCT_PROPOSAL_STATUS_LABELS = {
  pending: 'Venter',
  approved: 'Godkjent',
  rejected: 'Avvist',
  superseded: 'Utdatert',
};

const PRODUCT_INBOX_FIELD_TYPE_OPTIONS = [
  { value: 'string', label: 'Kort tekst' },
  { value: 'text', label: 'Lang tekst' },
  { value: 'integer', label: 'Heltall' },
  { value: 'boolean', label: 'Ja/nei' },
  { value: 'select', label: 'Valgliste' },
  { value: 'date', label: 'Dato' },
  { value: 'datetime', label: 'Dato og tid' },
];

const MAX_SELECT_OPTION_LENGTH = 120;

const normalizeSelectOptionsDraft = (value) => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[,\n]+/)
      : [];

  const normalized = [];
  const seen = new Set();
  source.forEach((entry) => {
    const option = String(entry || '').trim().slice(0, MAX_SELECT_OPTION_LENGTH);
    if (!option || seen.has(option)) return;
    seen.add(option);
    normalized.push(option);
  });
  return normalized;
};

const createEmptyProductInboxField = () => ({
  key: '',
  label: '',
  type: 'string',
  options: [],
  placeholder: '',
  helpText: '',
  defaultValue: '',
  required: false,
  collectInChat: false,
  showInInboxList: false,
});

const createEmptyProductInboxConfig = () => ({
  fields: [],
  summaryFieldKeys: [],
  updatedAt: null,
});

const createEmptyProductNotificationSettings = () => ({
  enabled: false,
  emailDestination: '',
  updatedAt: null,
});

const cloneProductInboxConfigFields = (fields) => (
  Array.isArray(fields)
    ? fields.map((field) => ({
      ...createEmptyProductInboxField(),
      ...field,
      options: normalizeSelectOptionsDraft(field?.options ?? field?.optionsText),
      defaultValue:
        typeof field?.defaultValue === 'boolean'
          ? String(field.defaultValue)
          : (field?.defaultValue ?? ''),
      required: field?.required === true,
      collectInChat: field?.collectInChat === true || field?.collect_in_chat === true,
      showInInboxList: field?.showInInboxList === true || field?.show_in_inbox_list === true || field?.showInList === true,
    }))
    : []
);

const cloneProductInboxConfig = (config) => ({
  ...createEmptyProductInboxConfig(),
  ...(config && typeof config === 'object' ? config : {}),
  fields: cloneProductInboxConfigFields(config?.fields),
  summaryFieldKeys: Array.isArray(config?.summaryFieldKeys)
    ? [...config.summaryFieldKeys]
    : Array.isArray(config?.summary_field_keys)
      ? [...config.summary_field_keys]
      : [],
});

const mapProductInboxField = (field) => ({
  key: field?.key || '',
  label: field?.label || '',
  type: field?.type || 'string',
  options: normalizeSelectOptionsDraft(field?.options ?? field?.optionsText),
  placeholder: field?.placeholder || '',
  helpText: field?.helpText || field?.help_text || '',
  defaultValue:
    typeof field?.defaultValue === 'boolean'
      ? String(field.defaultValue)
      : (field?.defaultValue ?? field?.default_value ?? ''),
  required: field?.required === true,
  collectInChat: field?.collectInChat === true || field?.collect_in_chat === true,
  showInInboxList: field?.showInInboxList === true || field?.show_in_inbox_list === true || field?.show_in_list === true,
});

const mapProductInboxConfig = (config) => {
  if (!config || typeof config !== 'object') return null;
  const fields = Array.isArray(config.fields) ? config.fields.map(mapProductInboxField) : [];
  return {
    fields,
    summaryFieldKeys: Array.isArray(config.summary_field_keys)
      ? config.summary_field_keys
      : Array.isArray(config.summaryFieldKeys)
        ? config.summaryFieldKeys
        : [],
    updatedAt: config.updated_at || config.updatedAt || null,
  };
};

const mapProductNotificationSettings = (settings) => {
  const source = settings && typeof settings === 'object' ? settings : {};
  return {
    enabled: source.enabled === true,
    emailDestination: source.emailDestination || source.email_destination || '',
    updatedAt: source.updatedAt || source.updated_at || null,
  };
};

const mapProductItem = (item) => ({
  id: item?.id ?? `product-${Math.random().toString(16).slice(2)}`,
  name: item?.name || '',
  shortDescription: item?.short_description || item?.shortDescription || '',
  category: item?.category || '',
  tags: Array.isArray(item?.tags) ? item.tags : [],
  priceAmount: item?.price_amount ?? item?.priceAmount ?? null,
  priceCurrency: item?.price_currency || item?.priceCurrency || '',
  priceDisplay: item?.price_display || item?.priceDisplay || '',
  url: item?.url || '',
  sku: item?.sku || '',
  imageUrl: item?.image_url || item?.imageUrl || '',
  sourceKind: item?.source_kind || item?.sourceKind || 'manual',
  sourceKey: item?.source_key || item?.sourceKey || null,
  externalProductId: item?.external_product_id || item?.externalProductId || null,
  chatTitle: item?.chat_title || item?.chatTitle || '',
  chatDescription: item?.chat_description || item?.chatDescription || '',
  status: item?.status || 'active',
  inboxConfig: mapProductInboxConfig(item?.inbox_config || item?.inboxConfig || null),
  notificationSettings: mapProductNotificationSettings(item?.notification_settings || item?.notificationSettings || null),
  createdAt: item?.created_at || item?.createdAt || null,
  updatedAt: item?.updated_at || item?.updatedAt || null,
});

const mapProductProposal = (item) => ({
  id: item?.id ?? `proposal-${Math.random().toString(16).slice(2)}`,
  syncRunId: item?.sync_run_id ?? item?.syncRunId ?? null,
  sourceKind: item?.source_kind || item?.sourceKind || 'website_scan',
  proposalType: item?.proposal_type || item?.proposalType || 'create',
  sourceKey: item?.source_key || item?.sourceKey || null,
  status: item?.status || 'pending',
  confidence: item?.confidence ?? null,
  evidence: Array.isArray(item?.evidence) ? item.evidence : [],
  proposedName: item?.proposed_name || item?.proposedName || '',
  proposedShortDescription: item?.proposed_short_description || item?.proposedShortDescription || '',
  proposedCategory: item?.proposed_category || item?.proposedCategory || '',
  proposedTags: Array.isArray(item?.proposed_tags) ? item.proposed_tags : Array.isArray(item?.proposedTags) ? item.proposedTags : [],
  proposedPriceAmount: item?.proposed_price_amount ?? item?.proposedPriceAmount ?? null,
  proposedPriceCurrency: item?.proposed_price_currency || item?.proposedPriceCurrency || '',
  proposedPriceDisplay: item?.proposed_price_display || item?.proposedPriceDisplay || '',
  proposedUrl: item?.proposed_url || item?.proposedUrl || '',
  proposedSku: item?.proposed_sku || item?.proposedSku || '',
  proposedImageUrl: item?.proposed_image_url || item?.proposedImageUrl || '',
  reason: item?.reason || '',
  reviewedAt: item?.reviewed_at || item?.reviewedAt || null,
  updatedAt: item?.updated_at || item?.updatedAt || null,
});

const formatCompactProductPrice = ({ priceDisplay, priceAmount, priceCurrency }) => {
  const display = String(priceDisplay || '').trim();
  if (display) return display;
  const numericAmount = Number(priceAmount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) return 'Ingen pris oppgitt';
  const normalizedCurrency = String(priceCurrency || '').trim().toUpperCase();
  return normalizedCurrency
    ? `${numericAmount} ${normalizedCurrency}`
    : `${numericAmount}`;
};

const createEmptyProductDraft = () => ({
  id: `new-product-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: '',
  shortDescription: '',
  priceAmount: '',
  priceCurrency: 'NOK',
  priceDisplay: '',
  sourceKind: 'manual',
  status: 'active',
  inboxConfig: createEmptyProductInboxConfig(),
  notificationSettings: createEmptyProductNotificationSettings(),
  isNew: true,
});

const createProductModalDraft = (product = null) => {
  const base = product ? mapProductItem(product) : createEmptyProductDraft();
  return {
    ...base,
    inboxConfig: cloneProductInboxConfig(base.inboxConfig),
    notificationSettings: {
      ...createEmptyProductNotificationSettings(),
      ...(base.notificationSettings || {}),
    },
  };
};

const buildProductPayload = (item) => ({
  name: String(item?.name || '').trim(),
  short_description: String(item?.shortDescription || '').trim() || null,
  price_amount: item?.priceAmount === '' || item?.priceAmount === null ? null : Number(item.priceAmount),
  price_currency: String(item?.priceCurrency || '').trim().toUpperCase() || null,
  price_display: String(item?.priceDisplay || '').trim() || null,
  source_kind: item?.sourceKind || 'manual',
  status: item?.status || 'active',
});

const buildProductInboxConfigPayload = (config) => {
  const fields = Array.isArray(config?.fields)
    ? config.fields.map((field, index) => {
      const normalizedType = String(field?.type || 'string').trim() || 'string';
      const normalizedOptions = normalizeSelectOptionsDraft(field?.options);
      const rawDefaultValue = field?.defaultValue;
      let defaultValue = rawDefaultValue;
      if (normalizedType === 'boolean') {
        defaultValue = rawDefaultValue === 'true' || rawDefaultValue === true
          ? true
          : rawDefaultValue === 'false' || rawDefaultValue === false
            ? false
            : null;
      } else if (normalizedType === 'integer') {
        defaultValue = rawDefaultValue === '' || rawDefaultValue === null || rawDefaultValue === undefined
          ? null
          : Number(rawDefaultValue);
      } else if (rawDefaultValue === '') {
        defaultValue = null;
      }
      return {
        key: String(field?.key || '').trim().toLowerCase(),
        label: String(field?.label || '').trim(),
        type: normalizedType,
        options: normalizedType === 'select'
          ? normalizedOptions
          : [],
        placeholder: String(field?.placeholder || '').trim() || null,
        helpText: String(field?.helpText || '').trim() || null,
        defaultValue,
        required: field?.required === true,
        collectInChat: field?.collectInChat === true,
        order: index,
        showInInboxList: field?.showInInboxList === true,
      };
    })
    : [];

  return {
    fields,
    summaryFieldKeys: fields.filter((field) => field.showInInboxList === true).map((field) => field.key),
  };
};

const buildProductNotificationSettingsPayload = (settings) => ({
  enabled: settings?.enabled === true,
  emailDestination: String(settings?.emailDestination || '').trim(),
});

const PRODUCT_NOTIFICATION_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const validateProductNotificationSettingsDraft = (settings) => {
  if (settings?.enabled !== true) return null;
  const emailDestination = String(settings?.emailDestination || '').trim();
  if (!emailDestination) return 'E-postmottaker er påkrevd når produktvarsling er aktivert.';
  if (!PRODUCT_NOTIFICATION_EMAIL_REGEX.test(emailDestination)) return 'E-postmottaker må være en gyldig e-postadresse.';
  return null;
};

const validateProductInboxConfigDraft = (config) => {
  const fields = Array.isArray(config?.fields) ? config.fields : [];
  const seenKeys = new Set();
  for (let index = 0; index < fields.length; index += 1) {
    const field = fields[index] || {};
    const key = String(field.key || '').trim().toLowerCase();
    const label = String(field.label || '').trim();
    const type = String(field.type || 'string').trim() || 'string';
    if (!key) {
      return `Produktattributt ${index + 1} mangler nøkkel.`;
    }
    if (!label) {
      return `Produktattributt ${index + 1} mangler etikett.`;
    }
    if (seenKeys.has(key)) {
      return `Nøkkelen "${key}" er brukt flere ganger i produktattributtene.`;
    }
    seenKeys.add(key);
    if (type === 'select') {
      const options = normalizeSelectOptionsDraft(field.options);
      if (options.length === 0) {
        return `Valglisten for "${label}" må ha minst ett alternativ.`;
      }
    }
    if (type === 'integer' && field.defaultValue !== '' && field.defaultValue !== null && field.defaultValue !== undefined) {
      const parsed = Number(field.defaultValue);
      if (!Number.isInteger(parsed)) {
        return `Standardverdien for "${label}" må være et heltall.`;
      }
    }
  }
  return null;
};

const WEBSITE_INSTALL_STATUS_META = {
  verified: { label: 'Verifisert', className: 'is-verified' },
  stale: { label: 'Utdatert', className: 'is-stale' },
  not_verified: { label: 'Ikke verifisert', className: 'is-not-verified' },
  unconfigured: { label: 'Ikke konfigurert', className: 'is-unconfigured' },
};

const WEBSITE_SCAN_ACTIVE_STATES = new Set(['queued', 'running']);
const WEBSITE_SCAN_EVENT_LABELS = {
  queued: 'Kø',
  started: 'Startet',
  discovery: 'Oppdagelse',
  crawl: 'Skanning',
  completed: 'Fullført',
  failed: 'Feilet',
};

const mapKnowledgeItem = (pair) => {
  const sourceType = pair?.source_type === 'website' ? 'website' : 'manual';
  const chunkIndexRaw = pair?.source_chunk_index;
  const sourceChunkIndex = chunkIndexRaw === null || chunkIndexRaw === undefined
    ? null
    : Number(chunkIndexRaw);
  const qualityRaw = pair?.source_quality_score;
  const sourceQualityScore = qualityRaw === null || qualityRaw === undefined
    ? null
    : Number(qualityRaw);
  return {
    id: pair?.id ?? `pair-${Math.random().toString(16).slice(2)}`,
    keyword: pair?.keyword || pair?.question || '',
    content: pair?.content || pair?.answer || '',
    sourceType,
    sourceUrl: pair?.source_url || null,
    sourceParentKey: pair?.source_parent_key || null,
    sourceChunkIndex: Number.isFinite(sourceChunkIndex) ? sourceChunkIndex : null,
    sourceQualityScore: Number.isFinite(sourceQualityScore) ? sourceQualityScore : null,
    sourceUpdatedAt: pair?.source_updated_at || null,
    readOnly: sourceType === 'website',
  };
};

const buildWebsiteGroupLabel = (sourceUrl, sourceParentKey) => {
  if (sourceUrl) {
    try {
      const parsed = new URL(sourceUrl);
      const path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
      return `${parsed.hostname}${path}`.slice(0, 120);
    } catch (_error) {
      return sourceUrl.slice(0, 120);
    }
  }
  if (sourceParentKey) {
    return `Webside ${sourceParentKey.slice(0, 10)}`;
  }
  return 'Webside';
};

const buildWebsiteKnowledgeGroups = (pairs = []) => {
  const map = new Map();
  pairs.forEach((pair) => {
    if (pair.sourceType !== 'website') return;
    const groupId = pair.sourceParentKey || pair.sourceUrl || `website-${pair.id}`;
    if (!map.has(groupId)) {
      map.set(groupId, {
        id: groupId,
        sourceUrl: pair.sourceUrl || null,
        sourceParentKey: pair.sourceParentKey || null,
        label: buildWebsiteGroupLabel(pair.sourceUrl, pair.sourceParentKey),
        updatedAt: pair.sourceUpdatedAt || null,
        items: [],
      });
    }
    const group = map.get(groupId);
    group.items.push(pair);
    if (pair.sourceUpdatedAt && (!group.updatedAt || pair.sourceUpdatedAt > group.updatedAt)) {
      group.updatedAt = pair.sourceUpdatedAt;
    }
  });

  return Array.from(map.values())
    .map((group) => ({
      ...group,
      items: group.items
        .slice()
        .sort((a, b) => {
          const aIndex = Number.isFinite(a.sourceChunkIndex) ? a.sourceChunkIndex : Number.MAX_SAFE_INTEGER;
          const bIndex = Number.isFinite(b.sourceChunkIndex) ? b.sourceChunkIndex : Number.MAX_SAFE_INTEGER;
          if (aIndex !== bIndex) return aIndex - bIndex;
          return String(a.keyword || '').localeCompare(String(b.keyword || ''));
        }),
    }))
    .sort((a, b) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('no-NO');
};

const parseIntegrationId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? String(value) : numeric;
};

const normalizeBotIntegrationAssignments = (rawAssignments = []) =>
  rawAssignments
    .map((assignment, index) => {
      const integrationId = parseIntegrationId(
        assignment.integration_id
        ?? assignment.integrationId
        ?? assignment.mcp_integration_id
        ?? assignment.mcpIntegrationId
        ?? null,
      );
      if (integrationId === null) return null;
      const instruction =
        typeof assignment.instruction === 'string'
          ? assignment.instruction
          : typeof assignment.notes === 'string'
            ? assignment.notes
            : '';
      const capabilities =
        assignment.capabilities && typeof assignment.capabilities === 'object'
          ? assignment.capabilities
          : {};
      const serviceNameHint =
        assignment.serviceName
        ?? assignment.service_name
        ?? assignment.mcpName
        ?? assignment.mcp_name
        ?? '';
      const clientKey = assignment.id ? `integration-${assignment.id}` : createClientKey();
      return {
        id: assignment.id ?? null,
        clientKey,
        integrationId,
        instruction,
        capabilities,
        serviceNameHint,
        orderIndex: assignment.order_index ?? index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map((assignment, index) => ({ ...assignment, orderIndex: index }));

const resolveLogoPreviewSrc = (value) => {
  if (!value) return '';
  if (value.startsWith('data:')) return value;
  if (/^https?:\/\//i.test(value)) return value;
  try {
    return new URL(value, DashboardConfig.apiBaseUrl).href;
  } catch (error) {
    return value;
  }
};

const normalizeBot = (bot) => {
  const botType = normalizeBotTypeValue(bot.bot_type ?? bot.botType ?? BOT_TYPE_GENERIC);
  const botProfileConfig = normalizeBotProfileConfigValue(
    botType,
    bot.bot_profile_config ?? bot.botProfileConfig ?? {},
  );
  const conversationPolicy = normalizeConversationPolicy(
    bot.conversation_policy ?? bot.conversationPolicy ?? {},
  );
  const rawExternalId = bot.external_id ?? bot.externalId ?? null;
  const externalId = rawExternalId ? String(rawExternalId) : '';
  const rawIntegrationId =
    bot.default_mcp_integration_id
    ?? bot.defaultIntegrationId
    ?? bot.mcpIntegrationId
    ?? null;
  let defaultIntegrationId = parseIntegrationId(rawIntegrationId);

  const rawAssignments = Array.isArray(bot.integration_assignments)
    ? bot.integration_assignments
    : Array.isArray(bot.integrations)
      ? bot.integrations
      : [];
  const integrationAssignments = normalizeBotIntegrationAssignments(rawAssignments);

  const assignedIds = integrationAssignments.map((assignment) => assignment.integrationId);
  if (assignedIds.length > 0) {
    const defaultMatch = assignedIds.some((id) => String(id) === String(defaultIntegrationId));
    if (!defaultMatch) {
      defaultIntegrationId = assignedIds[0];
    }
  } else {
    defaultIntegrationId = null;
  }

  const rawBookingScheduleId =
    bot.booking_schedule_id
    ?? bot.bookingScheduleId
    ?? bot.booking_schedule
    ?? null;
  const bookingScheduleIdParsed =
    rawBookingScheduleId === null || rawBookingScheduleId === undefined || rawBookingScheduleId === ''
      ? null
      : Number(rawBookingScheduleId);
  const bookingScheduleId = Number.isNaN(bookingScheduleIdParsed) ? null : bookingScheduleIdParsed;
  const rawMenuLinks = Array.isArray(bot.menu_links) ? bot.menu_links : [];
  const menuLinks = rawMenuLinks.map((link, index) => ({
    clientKey: createClientKey(),
    label: link?.label || '',
    url: link?.url || '',
    description: link?.description || '',
    icon: link?.icon || '',
    orderIndex: index,
  }));

  const rawBackgroundPreset =
    bot.background_preset
    ?? bot.backgroundPreset
    ?? null;
  const derivedBackgroundPresetId = (() => {
    if (rawBackgroundPreset?.id !== undefined) {
      const parsed = Number(rawBackgroundPreset.id);
      return Number.isNaN(parsed) ? null : parsed;
    }
    const raw = bot.background_preset_id ?? bot.backgroundPresetId ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  })();
  const backgroundCustomConfig =
    bot.background_custom_config
    ?? bot.backgroundCustomConfig
    ?? null;

  return {
    ...bot,
    botType,
    botProfileConfig,
    bot_type: botType,
    bot_profile_config: botProfileConfig,
    conversationPolicy,
    conversation_policy: conversationPolicy,
    externalId,
    uiConfig: bot.ui_config ?? bot.uiConfig ?? null,
    botTitle: bot.display_title || '',
    color: bot.theme_color || '#3451ff',
    gradientStart: bot.theme_gradient_start || bot.theme_color || '#1e255d',
    gradientEnd: bot.theme_gradient_end || bot.theme_color || '#2a9d8f',
    launcherColor:
      bot.launcher_bg_color
      || bot.theme_gradient_start
      || bot.theme_color
      || '#1a535c',
    panelBgColor:
      bot.panel_bg_color
      || bot.panelBgColor
      || bot.launcher_bg_color
      || '#1e3a5f',
    inputBgColor: bot.input_bg_color || bot.inputBgColor || bot.launcher_bg_color || '#f4f4f5',
    themePreset: bot.theme_preset || bot.themePreset || 'custom',
    welcomeMessage: bot.welcome_message || '',
    systemInstruction: bot.system_instruction || '',
    behavior: bot.behavior_notes || '',
    notificationEmail: bot.notification_email || bot.notificationEmail || '',
    logoUrl: bot.logo_url || bot.logoUrl || '',
    logoPreviewUrl: resolveLogoPreviewSrc(bot.logo_url || bot.logoUrl || ''),
    launcherIconUrl: bot.launcher_icon_url || bot.launcherIconUrl || '',
    launcherIconPreviewUrl: resolveLogoPreviewSrc(bot.launcher_icon_url || bot.launcherIconUrl || ''),
    launcherIconSize: normalizeLauncherIconSizeValue(bot.launcher_icon_size ?? bot.launcherIconSize ?? 64),
    proactiveMessage: bot.proactive_message || '',
    proactiveDelaySeconds:
      bot.proactive_delay_seconds === null || bot.proactive_delay_seconds === undefined
        ? '0'
        : String(bot.proactive_delay_seconds),
    proactiveCooldownSeconds:
      bot.proactiveCooldownSeconds
      ?? getProactiveCooldownFromUiConfig(bot.ui_config ?? bot.uiConfig),
    proactiveOpenChat: bot.proactiveOpenChat === true || getProactiveOpenChatFromUiConfig(bot.ui_config ?? bot.uiConfig),
    proactiveOpenChatDelaySeconds:
      bot.proactiveOpenChatDelaySeconds
      ?? getProactiveOpenChatDelayFromUiConfig(bot.ui_config ?? bot.uiConfig),
    menuIntroTitle: bot.menu_intro_title || '',
    menuIntroSubtitle: bot.menu_intro_subtitle || '',
    menuLinks,
    integrationAssignments,
    defaultIntegrationId,
    bookingScheduleId,
    backgroundPresetId: derivedBackgroundPresetId,
    backgroundPreset: rawBackgroundPreset || null,
    backgroundCustomConfig,
    websiteUrl: bot.website_url || bot.websiteUrl || '',
    websiteOrigin: bot.website_origin || bot.websiteOrigin || null,
    websiteScanEnabled: bot.website_scan_enabled === true || bot.websiteScanEnabled === true,
    websiteScanNextRunAt: bot.website_scan_next_run_at || bot.websiteScanNextRunAt || null,
    websiteScanLastSuccessAt: bot.website_scan_last_success_at || bot.websiteScanLastSuccessAt || null,
    websiteScanLastErrorAt: bot.website_scan_last_error_at || bot.websiteScanLastErrorAt || null,
    websiteScanLastErrorMessage: bot.website_scan_last_error_message || bot.websiteScanLastErrorMessage || null,
    allowedOrigins: Array.isArray(bot.allowed_origins) ? bot.allowed_origins : [],
    updated: bot.updated_at,
  };
};

const toPlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value;
};

const normalizeThemePresetEntry = (preset) => {
  if (!preset || typeof preset !== 'object') return null;
  const rawId = (preset.slug || preset.id || '').trim();
  if (!rawId) return null;
  const label = preset.name || preset.label || rawId;
  const description = preset.description || preset.text || '';
  const colorsSource = toPlainObject(preset.colors || preset.color_config);
  const backgroundPresetSlug =
    typeof colorsSource.backgroundPresetSlug === 'string'
      ? colorsSource.backgroundPresetSlug.trim().toLowerCase()
      : null;
  const normalizedColors = {
    gradientStart: colorsSource.gradientStart || colorsSource.gradient_start || '#1e255d',
    gradientEnd: colorsSource.gradientEnd || colorsSource.gradient_end || '#2a9d8f',
    color: colorsSource.color || colorsSource.accentColor || '#3451ff',
    launcherColor:
      colorsSource.launcherColor
      || colorsSource.launcher_color
      || colorsSource.gradientStart
      || colorsSource.gradient_start
      || '#1e255d',
    panelBgColor:
      colorsSource.panelBgColor
      || colorsSource.panel_bg_color
      || colorsSource.launcherColor
      || colorsSource.launcher_color
      || '#1e3a5f',
    inputBgColor:
      colorsSource.inputBgColor
      || colorsSource.input_bg_color
      || colorsSource.launcherColor
      || colorsSource.launcher_color
      || '#f4f4f5',
  };

  return {
    id: rawId,
    label,
    description,
    colors: normalizedColors,
    backgroundPresetSlug: backgroundPresetSlug || null,
    isCustom: Boolean(preset.isCustom),
    customerPresetId:
      preset.customerPresetId === null || preset.customerPresetId === undefined
        ? null
        : Number(preset.customerPresetId),
  };
};

const buildStandardGradientConfig = (bot) => {
  const start = bot.gradientStart || bot.color || '#1e255d';
  const end = bot.gradientEnd || bot.color || '#2a9d8f';
  return {
    variant: 'gradient',
    colors: [start, end],
    angle: 135,
  };
};

const BotsPage = () => {
  const { selectedCompanyId, selectedCompany, user } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  
  // Permission check for editing bots/knowledge
  const membershipRole = (selectedCompany?.membershipRole || '').toLowerCase();
  const isSuperAdmin = Boolean(selectedCompany?.isSuperAdminAccess) || Boolean(user?.isAdmin);
  const normalizedRole = selectedCompany?.isOwner || isSuperAdmin ? 'owner' : membershipRole;
  const canEditBot =
    normalizedRole === 'owner'
    || normalizedRole === 'manager'
    || normalizedRole === 'admin'
    || normalizedRole === 'builder';
  const [bots, setBots] = useState([]);
  const [botLimit, setBotLimit] = useState(null);
  const [companyDefaultBotType, setCompanyDefaultBotType] = useState(BOT_TYPE_GENERIC);
  const [expandedBot, setExpandedBot] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingBotId, setSavingBotId] = useState(null);
  const [error, setError] = useState(null);
  const [billingAccess, setBillingAccess] = useState(null);
  const [botToggleLoading, setBotToggleLoading] = useState({});
  const [installCopiedStep, setInstallCopiedStep] = useState(null);
  const [platformGuideModal, setPlatformGuideModal] = useState(null);
  const [platformIframeLoaded, setPlatformIframeLoaded] = useState(false);
  const [platformIframeFailed, setPlatformIframeFailed] = useState(false);
  const [logoUploadState, setLogoUploadState] = useState({});
  const [launcherIconUploadState, setLauncherIconUploadState] = useState({});
  const [bookingSchedules, setBookingSchedules] = useState([]);
  const [bookingSchedulesLoading, setBookingSchedulesLoading] = useState(false);
  const [bookingSchedulesError, setBookingSchedulesError] = useState(null);
  const [backgroundPresets, setBackgroundPresets] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [backgroundPresetsLoading, setBackgroundPresetsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [backgroundPresetsError, setBackgroundPresetsError] = useState(null);
  const [themePresets, setThemePresets] = useState(DEFAULT_THEME_PRESETS);
  const [themePresetsLoading, setThemePresetsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [themePresetsError, setThemePresetsError] = useState(null);
  const [activeSections, setActiveSections] = useState({});
  const [mcpTools, setMcpTools] = useState([]);
  const [mcpToolsLoading, setMcpToolsLoading] = useState(false);
  const [mcpToolsError, setMcpToolsError] = useState(null);
  const [tabsConfigByBot, setTabsConfigByBot] = useState({});
  const [tabsConfigLoadingByBot, setTabsConfigLoadingByBot] = useState({});
  const [tabsConfigSavingByBot, setTabsConfigSavingByBot] = useState({});
  const [tabsConfigTestingByBot, setTabsConfigTestingByBot] = useState({});
  const [tabsConfigErrorByBot, setTabsConfigErrorByBot] = useState({});
  const [tabsLessonOptionsByBot, setTabsLessonOptionsByBot] = useState({});
  const [tabsLessonOptionsLoadingByBot, setTabsLessonOptionsLoadingByBot] = useState({});
  const isBillingBlocked = Boolean(billingAccess?.blocked);

  // Setup wizard state (opened when a new bot is created)
  const [setupWizardOpen, setSetupWizardOpen] = useState(false);
  const [setupWizardBotId, setSetupWizardBotId] = useState(null);
  const [setupWizardStepIndex, setSetupWizardStepIndex] = useState(0);

  useEffect(() => {
    if (error) {
      notifyError(error, { source: 'Bots' });
    }
  }, [error]);

  const notifyBotSuccess = useCallback((botId, message, options = {}) => {
    notifySuccess(message, {
      source: 'Bots',
      dedupeKey: `bots:${botId || 'unknown'}:success:${message}`,
      ...options,
    });
  }, []);

  const notifyBotError = useCallback((botId, message, options = {}) => {
    notifyError(message, {
      source: 'Bots',
      dedupeKey: `bots:${botId || 'unknown'}:error:${message}`,
      ...options,
    });
  }, []);

  // Knowledge base state (per bot)
  const [knowledgePairs, setKnowledgePairs] = useState({}); // { [botId]: [...pairs] }
  const [knowledgeLoading, setKnowledgeLoading] = useState({}); // { [botId]: bool }
  const [knowledgeSaving, setKnowledgeSaving] = useState({}); // { [botId]: bool }
  const [knowledgeError, setKnowledgeError] = useState({}); // { [botId]: string }
  const [knowledgeExpandedId, setKnowledgeExpandedId] = useState({}); // { [botId]: pairId }
  const [knowledgeWebsiteExpandedId, setKnowledgeWebsiteExpandedId] = useState({}); // { [botId]: groupId }
  const [knowledgeGenerateOpen, setKnowledgeGenerateOpen] = useState({}); // { [botId]: bool }
  const [knowledgeGenerateText, setKnowledgeGenerateText] = useState({}); // { [botId]: string }
  const [knowledgeGenerateLoading, setKnowledgeGenerateLoading] = useState({}); // { [botId]: bool }
  const [knowledgeMaxEntries, setKnowledgeMaxEntries] = useState({}); // { [botId]: number|null }
  const [productItemsByBot, setProductItemsByBot] = useState({}); // { [botId]: [...items] }
  const [productProposalsByBot, setProductProposalsByBot] = useState({}); // { [botId]: [...proposals] }
  const [productSyncByBot, setProductSyncByBot] = useState({}); // { [botId]: sync }
  const [productLoadingByBot, setProductLoadingByBot] = useState({}); // { [botId]: bool }
  const [productSavingByBot, setProductSavingByBot] = useState({}); // { [botId]: bool }
  const [productErrorByBot, setProductErrorByBot] = useState({}); // { [botId]: string|null }
  const [productProposalExpandedIdByBot, setProductProposalExpandedIdByBot] = useState({}); // { [botId]: proposalId|null }
  const [productModalState, setProductModalState] = useState(null); // { botId, mode, productId, draft }
  const [websiteScanStatus, setWebsiteScanStatus] = useState({}); // { [botId]: statusPayload }
  const [websiteScanLoading, setWebsiteScanLoading] = useState({}); // { [botId]: bool }
  const [websiteScanError, setWebsiteScanError] = useState({}); // { [botId]: string|null }
  const [websiteScanRequesting, setWebsiteScanRequesting] = useState({}); // { [botId]: bool }
  const websiteScanFetchInFlight = useRef({});
  const websiteScanTimelineRefs = useRef({});

  useEffect(() => {
    if (!setupWizardOpen) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [setupWizardOpen]);

  useEffect(() => {
    if (!productModalState) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [productModalState]);

  useEffect(() => {
    if (!companyId) {
      setBots([]);
      setBotLimit(null);
      setCompanyDefaultBotType(BOT_TYPE_GENERIC);
      setExpandedBot(null);
      setLoading(false);
      setBillingAccess(null);
      setBackgroundPresets([]);
      setBackgroundPresetsError(null);
      setBackgroundPresetsLoading(false);
      setWebsiteScanStatus({});
      setWebsiteScanLoading({});
      setWebsiteScanError({});
      setWebsiteScanRequesting({});
      websiteScanFetchInFlight.current = {};
      setTabsConfigByBot({});
      setTabsConfigLoadingByBot({});
      setTabsConfigSavingByBot({});
      setTabsConfigErrorByBot({});
      setProductItemsByBot({});
      setProductProposalsByBot({});
      setProductSyncByBot({});
      setProductLoadingByBot({});
      setProductSavingByBot({});
      setProductErrorByBot({});
      setProductProposalExpandedIdByBot({});
      setProductModalState(null);
      return undefined;
    }

    let isMounted = true;

    async function loadBots() {
      setLoading(true);
      try {
        const response = await DashboardApi.getBots(null, companyId);
        if (!isMounted) return;
        const normalized = Array.isArray(response?.bots)
          ? response.bots.map(normalizeBot)
          : Array.isArray(response)
            ? response.map(normalizeBot)
            : [];
        setBots(normalized);
        setBillingAccess(response?.billingAccess || null);
        const rawBotLimit = response?.botLimit;
        const normalizedLimit = Number(rawBotLimit);
        setBotLimit(Number.isFinite(normalizedLimit) ? normalizedLimit : null);
        setCompanyDefaultBotType(
          normalizeBotTypeValue(
            response?.default_bot_type
            ?? response?.defaultBotType
            ?? BOT_TYPE_GENERIC,
          ),
        );
        const firstId = normalized[0]?.id || null;
        setExpandedBot(firstId);
        if (firstId) {
          setActiveSections((prev) => (prev[firstId] ? prev : { ...prev, [firstId]: 'overview' }));
        }
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        const message = err.message || 'Kunne ikke hente botdata.';
        setError(message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    async function loadBookingSchedules() {
      setBookingSchedulesLoading(true);
      try {
        const response = await DashboardApi.getBookingSchedules(companyId);
        if (!isMounted) return;
        const schedules = Array.isArray(response?.schedules)
          ? response.schedules.map((schedule) => ({
            id: Number(schedule.id),
            name: schedule.name,
          }))
          : [];
        setBookingSchedules(schedules);
        setBookingSchedulesError(null);
      } catch (err) {
        if (!isMounted) return;
        setBookingSchedules([]);
        const message = err.message || 'Kunne ikke hente bookingplaner.';
        setBookingSchedulesError(message);
        notifyError(message, { source: 'Bots' });
      } finally {
        if (isMounted) setBookingSchedulesLoading(false);
      }
    }

    async function loadBackgroundPresets() {
      setBackgroundPresetsLoading(true);
      try {
        const response = await DashboardApi.getBackgroundPresets();
        if (!isMounted) return;
        const presets = Array.isArray(response?.presets)
          ? response.presets.map((preset) => ({
            ...preset,
            id: Number(preset.id),
            tags: Array.isArray(preset.tags) ? preset.tags : [],
          }))
          : [];
        setBackgroundPresets(presets);
        setBackgroundPresetsError(null);
      } catch (err) {
        if (!isMounted) return;
        setBackgroundPresets([]);
        const message = err.message || 'Kunne ikke hente bakgrunnspreset.';
        setBackgroundPresetsError(message);
        notifyError(message, { source: 'Bots' });
      } finally {
        if (isMounted) setBackgroundPresetsLoading(false);
      }
    }

    async function loadMcpTools() {
      setMcpToolsLoading(true);
      try {
        const response = await DashboardApi.getTools(companyId);
        if (!isMounted) return;
        const tools = Array.isArray(response?.tools) ? response.tools : [];
        setMcpTools(tools);
        setMcpToolsError(null);
      } catch (err) {
        if (!isMounted) return;
        setMcpTools([]);
        const message = err.message || 'Kunne ikke hente verktøy.';
        setMcpToolsError(message);
        notifyError(message, { source: 'Bots' });
      } finally {
        if (isMounted) setMcpToolsLoading(false);
      }
    }

    loadBots();
    loadBookingSchedules();
    loadBackgroundPresets();
    loadMcpTools();
    return () => {
      isMounted = false;
    };
  }, [companyId]);

  const bookingScheduleOptions = useMemo(() => {
    if (!bookingSchedules.length) return [];
    return [...bookingSchedules].sort((a, b) => a.name.localeCompare(b.name, 'nb-NO'));
  }, [bookingSchedules]);

  const applyThemePresetResponse = useCallback((response) => {
    // Get remote presets from database (custom user-created themes)
    const remotePresets = Array.isArray(response?.presets)
      ? response.presets.map(normalizeThemePresetEntry).filter(Boolean)
      : [];

    // Get all built-in preset IDs to avoid duplicates
    const builtInIds = new Set(DEFAULT_THEME_PRESETS.map((p) => p.id));

    // Filter remote presets to only include user-created ones (not duplicates of built-ins)
    const userPresets = remotePresets.filter((preset) => {
      const id = preset.id || '';
      return id && !builtInIds.has(id);
    });

    // Mark user presets as custom (deletable)
    const markedUserPresets = userPresets.map((preset) => ({
      ...preset,
      isCustom: true,
    }));

    // Merge: all built-in presets + user-created presets
    setThemePresets([...DEFAULT_THEME_PRESETS, ...markedUserPresets]);
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadThemePresets() {
      setThemePresetsLoading(true);
      try {
        const response = await DashboardApi.getThemePresets(companyId);
        if (!isMounted) return;
        applyThemePresetResponse(response);
        setThemePresetsError(null);
      } catch (err) {
        if (!isMounted) return;
        setThemePresets(DEFAULT_THEME_PRESETS);
        const message = err.message || 'Kunne ikke hente temaer.';
        setThemePresetsError(message);
        notifyError(message, { source: 'Bots' });
      } finally {
        if (isMounted) setThemePresetsLoading(false);
      }
    }
    loadThemePresets();
    return () => {
      isMounted = false;
    };
  }, [companyId, applyThemePresetResponse]);


  const filteredBots = useMemo(() => {
    if (!searchTerm) return bots;
    const term = searchTerm.trim().toLowerCase();
    return bots.filter((bot) => {
      return (
        bot.name.toLowerCase().includes(term) ||
        (bot.segment || '').toLowerCase().includes(term) ||
        (bot.status || '').toLowerCase().includes(term)
      );
    });
  }, [bots, searchTerm]);

  const botCount = bots.length;
  const hasBotLimit = Number.isFinite(botLimit) && botLimit > 0;
  const botLimitReached = hasBotLimit && botCount >= botLimit;
  const botLimitLabel = hasBotLimit
    ? `${botCount} / ${botLimit}`
    : `${botCount} / Ubegrenset`;

  const toggleExpanded = (botId) => {
    setExpandedBot((current) => {
      const next = current === botId ? null : botId;
      if (next) {
        setActiveSections((prev) => (prev[next] ? prev : { ...prev, [next]: 'overview' }));
      }
      return next;
    });
  };

  const COLOR_CONTROL_FIELDS = [
    'gradientStart',
    'gradientEnd',
    'color',
    'launcherColor',
    'inputBgColor',
  ];

  const handleConfigChange = (botId, field, rawValue, options = {}) => {
    const { skipPresetReset = false } = options;
    const isString = typeof rawValue === 'string';
    const value = isString ? rawValue : rawValue;
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const mappedField = fieldMap[field] || field;
        const updated = {
          ...bot,
          [field]: isString ? value : value,
          [mappedField]: isString ? value : value,
        };
        if (field === 'logoUrl') {
          updated.logoPreviewUrl = resolveLogoPreviewSrc(isString ? rawValue.trim() : '');
        }
        if (field === 'launcherIconUrl') {
          updated.launcherIconPreviewUrl = resolveLogoPreviewSrc(isString ? rawValue.trim() : '');
        }
        if (field === 'launcherIconSize') {
          updated.launcherIconSize = normalizeLauncherIconSizeValue(rawValue);
          updated.launcher_icon_size = updated.launcherIconSize;
        }
        if (COLOR_CONTROL_FIELDS.includes(field) && !skipPresetReset) {
          updated.themePreset = 'custom';
        }
        if (
          ['gradientStart', 'gradientEnd', 'color'].includes(field)
          && (updated.backgroundPresetId === null || updated.backgroundPresetId === undefined)
        ) {
          updated.backgroundCustomConfig = buildStandardGradientConfig(updated);
        }
        return updated;
      }),
    );
  };

  const handleMenuLinkAdd = (botId) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const nextLinks = [
          ...(bot.menuLinks || []),
          {
            clientKey: createClientKey(),
            label: '',
            url: '',
            description: '',
            icon: '',
            orderIndex: (bot.menuLinks || []).length,
          },
        ];
        return {
          ...bot,
          menuLinks: nextLinks,
        };
      }),
    );
  };

  const handleMenuLinkChange = (botId, clientKey, field, value) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const nextLinks = (bot.menuLinks || []).map((link) =>
          link.clientKey === clientKey ? { ...link, [field]: value } : link,
        );
        return {
          ...bot,
          menuLinks: nextLinks,
        };
      }),
    );
  };

  const handleMenuLinkRemove = (botId, clientKey) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const nextLinks = (bot.menuLinks || []).filter((link) => link.clientKey !== clientKey);
        return {
          ...bot,
          menuLinks: nextLinks,
        };
      }),
    );
  };

  // Allowed Origins handlers
  const handleAllowedOriginAdd = (botId) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        return {
          ...bot,
          allowedOrigins: [...(bot.allowedOrigins || []), ''],
        };
      }),
    );
  };

  const handleAllowedOriginChange = (botId, index, value) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const nextOrigins = [...(bot.allowedOrigins || [])];
        nextOrigins[index] = value;
        return {
          ...bot,
          allowedOrigins: nextOrigins,
        };
      }),
    );
  };

  const handleAllowedOriginRemove = (botId, index) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const nextOrigins = (bot.allowedOrigins || []).filter((_, i) => i !== index);
        return {
          ...bot,
          allowedOrigins: nextOrigins,
        };
      }),
    );
  };

  const handleBookingScheduleChange = (botId, rawValue) => {
    const parsedValue = rawValue === '' ? null : Number(rawValue);
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        return {
          ...bot,
          bookingScheduleId: Number.isNaN(parsedValue) ? null : parsedValue,
        };
      }),
    );
  };

  const handleBackgroundPresetSelect = (botId, presetId) => {
    setBots((prev) =>
      prev.map((bot) => {
        if (bot.id !== botId) return bot;
        const matched =
          presetId === null
            ? null
            : backgroundPresets.find(
              (preset) => Number(preset.id) === Number(presetId),
            ) || bot.backgroundPreset;
        return {
          ...bot,
          backgroundPresetId: presetId === null ? null : Number(presetId),
          backgroundPreset: matched || null,
          backgroundCustomConfig:
            presetId === null
              ? buildStandardGradientConfig({
                ...bot,
                backgroundPresetId: null,
                backgroundPreset: null,
              })
              : null,
        };
      }),
    );
  };

  const handleThemePresetSelect = (botId, presetId) => {
    handleConfigChange(botId, 'themePreset', presetId);
    if (presetId === 'custom') {
      return;
    }
    const preset = themePresets.find((entry) => entry.id === presetId);
    if (!preset?.colors) return;
    Object.entries(preset.colors).forEach(([key, colorValue]) => {
      handleConfigChange(botId, key, colorValue, { skipPresetReset: true });
    });
    // If preset has a direct backgroundConfig, use it (bypasses database)
    if (preset.backgroundConfig) {
      setBots((prev) =>
        prev.map((bot) =>
          bot.id === botId
            ? {
                ...bot,
                backgroundPresetId: null,
                backgroundPreset: null,
                backgroundCustomConfig: preset.backgroundConfig,
              }
            : bot,
        ),
      );
    } else if (preset.backgroundPresetSlug) {
      // Fall back to database lookup for backwards compatibility
      const matchedBackground = backgroundPresets.find(
        (entry) =>
          (entry.slug || '').toLowerCase() === preset.backgroundPresetSlug.toLowerCase(),
      );
      if (matchedBackground) {
        handleBackgroundPresetSelect(botId, matchedBackground.id);
      }
    }
  };

  const handleSave = async (bot) => {
    setSavingBotId(bot.id);
    try {
      const trimString = (value) => (typeof value === 'string' ? value.trim() : value);
      const normalizedProactiveMessage = (() => {
        const text = trimString(bot.proactiveMessage);
        return text ? text : null;
      })();
      const normalizedProactiveDelay = normalizeDelaySeconds(bot.proactiveDelaySeconds);
      const normalizedProactiveCooldown = normalizeDelaySeconds(bot.proactiveCooldownSeconds);
      const normalizedProactiveOpenChatDelay = normalizeDelaySeconds(bot.proactiveOpenChatDelaySeconds);
      const payload = {
        name: trimString(bot.name),
        display_title: trimString(bot.botTitle),
        status: bot.status,
        segment: trimString(bot.segment),
        bot_type: normalizeBotTypeValue(bot.botType || bot.bot_type),
        bot_profile_config: normalizeBotProfileConfigValue(
          bot.botType || bot.bot_type,
          bot.botProfileConfig || bot.bot_profile_config || {},
        ),
        conversation_policy: normalizeConversationPolicy(
          bot.conversationPolicy || bot.conversation_policy || {},
        ),
        theme_color: bot.color,
        theme_gradient_start: trimString(bot.gradientStart),
        theme_gradient_end: trimString(bot.gradientEnd),
        launcher_bg_color: trimString(bot.launcherColor),
        input_bg_color: trimString(bot.inputBgColor),
        theme_preset: trimString(bot.themePreset || 'custom'),
        background_preset_id:
          bot.backgroundPresetId === null || bot.backgroundPresetId === undefined
            ? null
            : bot.backgroundPresetId,
        background_custom_config:
          bot.backgroundPresetId === null || bot.backgroundPresetId === undefined
            ? (bot.backgroundCustomConfig && Object.keys(bot.backgroundCustomConfig).length
              ? bot.backgroundCustomConfig
              : buildStandardGradientConfig(bot))
            : bot.backgroundCustomConfig ?? null,
        welcome_message: trimString(bot.welcomeMessage),
        system_instruction: trimString(bot.systemInstruction),
        behavior_notes: trimString(bot.behavior),
        notification_email: trimString(bot.notificationEmail),
        logo_url: trimString(bot.logoUrl),
        launcher_icon_url: trimString(bot.launcherIconUrl) || null,
        launcher_icon_size: normalizeLauncherIconSizeValue(bot.launcherIconSize),
        booking_schedule_id:
          bot.bookingScheduleId === null || bot.bookingScheduleId === undefined
            ? null
            : bot.bookingScheduleId,
        proactive_message: normalizedProactiveMessage,
        proactive_delay_seconds: normalizedProactiveDelay,
        menu_intro_title: trimString(bot.menuIntroTitle),
        menu_intro_subtitle: trimString(bot.menuIntroSubtitle),
        menu_links: (bot.menuLinks || [])
          .map((link) => ({
            label: trimString(link.label),
            url: trimString(link.url),
            description: trimString(link.description),
            icon: trimString(link.icon),
          }))
          .filter((link) => link.label && link.url),
        ui_config: withProactiveBehaviorUiConfig({
          uiConfig: bot.uiConfig,
          proactiveCooldownSeconds: normalizedProactiveCooldown,
          proactiveOpenChat: bot.proactiveOpenChat,
          proactiveOpenChatDelaySeconds: normalizedProactiveOpenChatDelay,
        }),
        website_url: trimString(bot.websiteUrl) || null,
        website_scan_enabled: bot.websiteScanEnabled === true,
        allowed_origins: (bot.allowedOrigins || [])
          .map((origin) => (typeof origin === 'string' ? origin.trim() : ''))
          .filter((origin) => origin.length > 0),
      };

      const updated = await DashboardApi.updateBot(bot.id, payload);
      setBots((prev) =>
        prev.map((item) => (item.id === bot.id ? normalizeBot(updated) : item)),
      );
      await loadWebsiteScanStatus(bot.id, { silent: true });

      notifyBotSuccess(bot.id, 'Endringene er lagret.');
    } catch (err) {
      setError(err.message || 'Kunne ikke lagre endringer.');
    } finally {
      setSavingBotId(null);
    }
  };

  const handleBotToggle = async (bot) => {
    if (!bot || !canEditBot) return;
    if (isBillingBlocked) {
      setError('Betalingen feilet. Botene er låst til betalingen er oppdatert.');
      return;
    }
    const currentStatus = (bot.status || '').toLowerCase();
    const isActive = currentStatus !== 'deaktivert';
    const nextStatus = isActive ? 'deaktivert' : 'live';
    setBotToggleLoading((prev) => ({ ...prev, [bot.id]: true }));
    try {
      const updated = await DashboardApi.updateBot(bot.id, { status: nextStatus });
      setBots((prev) =>
        prev.map((item) => (item.id === bot.id ? normalizeBot(updated) : item)),
      );
    } catch (err) {
      setError(err.message || 'Kunne ikke oppdatere bot-status.');
    } finally {
      setBotToggleLoading((prev) => {
        const next = { ...prev };
        delete next[bot.id];
        return next;
      });
    }
  };

  const handleLogoUpload = async (bot, file) => {
    if (!bot?.id || !file) return;
    if (!companyId) {
      setError('Ingen aktiv bruker er valgt.');
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setLogoUploadState((prev) => ({
        ...prev,
        [bot.id]: { uploading: false, error: 'Logoen kan maks være 2 MB.' },
      }));
      return;
    }

    setLogoUploadState((prev) => ({
      ...prev,
      [bot.id]: { uploading: true, error: null },
    }));

    try {
      const result = await DashboardApi.uploadBotLogo(companyId, bot.id, file);
      setBots((prev) =>
        prev.map((item) => {
          if (item.id !== bot.id) return item;
          const preferred = result?.absoluteUrl || result?.url || item.logoUrl;
          const nextLogo = typeof preferred === 'string' ? preferred : item.logoUrl;
          const preview = resolveLogoPreviewSrc(nextLogo);
          return {
            ...item,
            logoUrl: nextLogo,
            logoPreviewUrl: preview,
          };
        }),
      );
      setLogoUploadState((prev) => ({
        ...prev,
        [bot.id]: { uploading: false, error: null },
      }));
      notifyBotSuccess(bot.id, 'Logoen ble oppdatert.');
    } catch (uploadError) {
      const message = uploadError?.message || 'Kunne ikke laste opp logoen.';
      setLogoUploadState((prev) => ({
        ...prev,
        [bot.id]: { uploading: false, error: message },
      }));
      notifyBotError(bot.id, message);
    }
  };

  const handleLauncherIconUpload = async (bot, file) => {
    if (!bot?.id || !file) return;
    if (!companyId) {
      setError('Ingen aktiv bruker er valgt.');
      return;
    }

    setLauncherIconUploadState((prev) => ({
      ...prev,
      [bot.id]: { uploading: true, error: null },
    }));

    try {
      const uploadFile = await downscaleImageFile(file, {
        maxBytes: MAX_LOGO_SIZE,
        maxDimension: 512,
      });
      if (uploadFile.size > MAX_LOGO_SIZE) {
        const message = canDownscaleImageFile(file)
          ? 'Chatikonet er fortsatt over 2 MB etter nedskalering. Prøv en mindre fil.'
          : 'Chatikonet kan maks være 2 MB.';
        throw new Error(message);
      }

      const result = await DashboardApi.uploadBotLauncherIcon(companyId, bot.id, uploadFile);
      setBots((prev) =>
        prev.map((item) => {
          if (item.id !== bot.id) return item;
          const preferred = result?.absoluteUrl || result?.url || item.launcherIconUrl;
          const nextIcon = typeof preferred === 'string' ? preferred : item.launcherIconUrl;
          return {
            ...item,
            launcherIconUrl: nextIcon,
            launcher_icon_url: nextIcon,
            launcherIconPreviewUrl: resolveLogoPreviewSrc(nextIcon),
          };
        }),
      );
      setLauncherIconUploadState((prev) => ({
        ...prev,
        [bot.id]: { uploading: false, error: null },
      }));
      notifyBotSuccess(
        bot.id,
        uploadFile !== file ? 'Chatikonet ble skalert ned og oppdatert.' : 'Chatikonet ble oppdatert.',
      );
    } catch (uploadError) {
      const message = uploadError?.message || 'Kunne ikke laste opp chatikonet.';
      setLauncherIconUploadState((prev) => ({
        ...prev,
        [bot.id]: { uploading: false, error: message },
      }));
      notifyBotError(bot.id, message);
    }
  };

  const handleDelete = async (bot) => {
    if (!bot?.id) return;
    const shouldDelete = window.confirm(`Er du sikker på at du vil slette ${bot.name}? Dette kan ikke angres.`);
    if (!shouldDelete) return;

    setSavingBotId(bot.id);
    setError(null);
    try {
      await DashboardApi.deleteBot(bot.id);
      setBots((prev) => prev.filter((item) => item.id !== bot.id));
      notifyBotSuccess(bot.id, 'Boten ble slettet.');
      setExpandedBot(null);
    } catch (err) {
      setError(err.message || 'Kunne ikke slette bot.');
    } finally {
      setSavingBotId(null);
    }
  };

  const handlePreview = (bot) => {
    if (!bot?.externalId) return;
    const previewUrl = DashboardConfig.getChatPreviewUrl(bot.externalId);
    window.open(previewUrl, '_blank', 'noopener');
  };

  const handleCopyEmbed = async (bot, stepId = 'embed') => {
    if (!bot?.externalId) return;
    const snippet = DashboardConfig.getChatEmbedSnippet(bot.externalId);
    try {
      await navigator.clipboard.writeText(snippet);
      setInstallCopiedStep(stepId);
      notifyBotSuccess(bot.id, 'Embed-koden er kopiert til utklippstavlen.');
      setTimeout(() => {
        setInstallCopiedStep(null);
      }, 2500);
    } catch (err) {
      console.error('Could not copy embed snippet', err);
      setError('Kunne ikke kopiere embed-koden.');
    }
  };

  const openSetupWizardForBot = (botId) => {
    if (!botId) return;
    setSetupWizardBotId(botId);
    setSetupWizardStepIndex(0);
    setSetupWizardOpen(true);
  };

  const closeSetupWizard = (options = {}) => {
    const { expandAfterClose = true } = options;
    const botId = setupWizardBotId;
    setSetupWizardOpen(false);
    setSetupWizardStepIndex(0);
    setSetupWizardBotId(null);
    if (expandAfterClose && botId) {
      setExpandedBot(botId);
      setActiveSections((prev) => ({ ...prev, [botId]: 'overview' }));
    }
  };

  const handleSetupWizardBack = () => {
    setSetupWizardStepIndex((prev) => Math.max(0, prev - 1));
  };

  const handleSetupWizardNext = async () => {
    const step = BOT_WIZARD_STEPS[setupWizardStepIndex];
    const bot = setupWizardBotId ? bots.find((item) => item.id === setupWizardBotId) : null;
    if (!bot) return;

    // Persist changes as the user moves forward, so the wizard is fully functional.
    // Note: Kunnskapsbase has its own save button.
    if (canEditBot && step?.sectionId !== 'schedules') {
      await handleSave(bot);
    }

    setSetupWizardStepIndex((prev) => Math.min(BOT_WIZARD_STEPS.length - 1, prev + 1));
  };

  const handleSetupWizardFinish = async () => {
    const bot = setupWizardBotId ? bots.find((item) => item.id === setupWizardBotId) : null;
    if (bot && canEditBot) {
      await handleSave(bot);
    }
    closeSetupWizard({ expandAfterClose: true });
  };

  const handleCreateBot = async () => {
    if (!companyId || !companyId) return;
    setSavingBotId('new');
    setError(null);
    try {
      const created = await DashboardApi.createBot(companyId, {
        companyId,
        bot_type: normalizeBotTypeValue(companyDefaultBotType),
      });
      const normalized = normalizeBot(created);
      setBots((prev) => [...prev, normalized]);
      setExpandedBot(created.id);
      if (created?.id) {
        setActiveSections((prev) => ({ ...prev, [created.id]: 'overview' }));
        openSetupWizardForBot(created.id);
      }
    } catch (err) {
      setError(err.message || 'Kunne ikke opprette bot.');
    } finally {
      setSavingBotId(null);
    }
  };

  const loadWebsiteScanStatus = useCallback(async (botId, { silent = false } = {}) => {
    if (!botId) return;
    if (websiteScanFetchInFlight.current[botId]) return;
    websiteScanFetchInFlight.current[botId] = true;
    if (!silent) {
      setWebsiteScanLoading((prev) => ({ ...prev, [botId]: true }));
    }
    try {
      const statusPayload = await DashboardApi.getBotWebsiteScanStatus(botId);
      setWebsiteScanStatus((prev) => ({ ...prev, [botId]: statusPayload }));
      setWebsiteScanError((prev) => ({ ...prev, [botId]: null }));
    } catch (err) {
      setWebsiteScanError((prev) => ({
        ...prev,
        [botId]: err.message || 'Kunne ikke hente nettsideskann-status.',
      }));
    } finally {
      websiteScanFetchInFlight.current[botId] = false;
      setWebsiteScanLoading((prev) => ({ ...prev, [botId]: false }));
    }
  }, []);

  const handleVerifyWebsiteInstallation = useCallback(async (botId) => {
    if (!botId) return;
    setWebsiteScanLoading((prev) => ({ ...prev, [botId]: true }));
    setWebsiteScanError((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.verifyBotWebsiteInstallation(botId);
      if (response?.status) {
        setWebsiteScanStatus((prev) => ({ ...prev, [botId]: response.status }));
      } else {
        await loadWebsiteScanStatus(botId, { silent: true });
      }

      if (response?.verified) {
        notifyBotSuccess(botId, 'Nettsiden er verifisert.');
      } else {
        const verificationMessage = response?.verification?.message
          || response?.status?.scan?.guardrail?.message
          || 'Fant ikke chatbot-installasjonen på nettsiden.';
        setWebsiteScanError((prev) => ({ ...prev, [botId]: verificationMessage }));
        notifyBotError(botId, verificationMessage);
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke sjekke nettsideinstallasjon.';
      setWebsiteScanError((prev) => ({
        ...prev,
        [botId]: message,
      }));
      notifyBotError(botId, message);
    } finally {
      setWebsiteScanLoading((prev) => ({ ...prev, [botId]: false }));
    }
  }, [loadWebsiteScanStatus, notifyBotError, notifyBotSuccess]);

  const handleRequestWebsiteScan = useCallback(async (botId) => {
    if (!botId || !canEditBot) return;
    setWebsiteScanRequesting((prev) => ({ ...prev, [botId]: true }));
    setWebsiteScanError((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.requestBotWebsiteScan(botId);
      if (response?.status) {
        setWebsiteScanStatus((prev) => ({ ...prev, [botId]: response.status }));
      } else {
        await loadWebsiteScanStatus(botId, { silent: true });
      }
      notifyBotSuccess(botId, 'Nettsideskann er lagt i kø.');
    } catch (err) {
      const message = err.message || 'Kunne ikke starte nettsideskann.';
      setWebsiteScanError((prev) => ({
        ...prev,
        [botId]: message,
      }));
      notifyBotError(botId, message);
    } finally {
      setWebsiteScanRequesting((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, loadWebsiteScanStatus, notifyBotError, notifyBotSuccess]);

  const activeOverviewBotId = useMemo(() => {
    if (!expandedBot) return null;
    const sectionId = activeSections[expandedBot] || 'overview';
    if (sectionId !== 'overview') return null;
    return expandedBot;
  }, [expandedBot, activeSections]);

  useEffect(() => {
    if (!activeOverviewBotId) return;
    loadWebsiteScanStatus(activeOverviewBotId);
  }, [activeOverviewBotId, loadWebsiteScanStatus]);

  useEffect(() => {
    if (!activeOverviewBotId) return undefined;
    const status = websiteScanStatus[activeOverviewBotId];
    const activeState = status?.scan?.activeJob?.status || null;
    if (!WEBSITE_SCAN_ACTIVE_STATES.has(activeState)) {
      return undefined;
    }
    const timer = setInterval(() => {
      loadWebsiteScanStatus(activeOverviewBotId, { silent: true });
    }, 2500);
    return () => clearInterval(timer);
  }, [activeOverviewBotId, websiteScanStatus, loadWebsiteScanStatus]);

  useEffect(() => {
    if (!activeOverviewBotId) return;
    const status = websiteScanStatus[activeOverviewBotId];
    const activeState = status?.scan?.activeJob?.status || null;
    if (!WEBSITE_SCAN_ACTIVE_STATES.has(activeState)) return;
    const timelineNode = websiteScanTimelineRefs.current[activeOverviewBotId];
    if (!timelineNode) return;
    requestAnimationFrame(() => {
      timelineNode.scrollTop = timelineNode.scrollHeight;
    });
  }, [activeOverviewBotId, websiteScanStatus]);

  const loadTabsConfigForBot = useCallback(async (botId, options = {}) => {
    const force = options.force === true;
    if (!botId) {
      return null;
    }
    if (!force && tabsConfigByBot[botId]?.loaded) {
      return tabsConfigByBot[botId];
    }
    if (!force && tabsConfigLoadingByBot[botId]) {
      return null;
    }

    setTabsConfigLoadingByBot((prev) => ({ ...prev, [botId]: true }));
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.getBotTabsConfig(botId);
      const nextDraft = createTabsConfigDraft(response || {});
      setTabsConfigByBot((prev) => ({
        ...prev,
        [botId]: nextDraft,
      }));
      return nextDraft;
    } catch (err) {
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: err.message || 'Kunne ikke hente TABS-oppsett.',
      }));
      return null;
    } finally {
      setTabsConfigLoadingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [tabsConfigByBot, tabsConfigLoadingByBot]);

  useEffect(() => {
    if (!expandedBot) return;
    const activeSection = activeSections[expandedBot] || 'overview';
    if (activeSection !== 'tools') return;

    const tabsTool = mcpTools.find((tool) => tool.name === TABS_TOOL_NAME);
    if (!tabsTool) return;

    if (tabsConfigByBot[expandedBot]?.loaded || tabsConfigLoadingByBot[expandedBot]) return;
    loadTabsConfigForBot(expandedBot);
  }, [
    expandedBot,
    activeSections,
    mcpTools,
    tabsConfigByBot,
    tabsConfigLoadingByBot,
    loadTabsConfigForBot,
  ]);

  const handleTabsConfigFieldChange = useCallback((botId, field, value) => {
    if (!botId || !field) return;

    setTabsConfigByBot((prev) => {
      const existing = prev[botId] || createTabsConfigDraft();
      const nextLessonDefaults = field === 'officeId'
        ? createTabsLessonDefaultsDraft(null)
        : existing.lessonDefaults || createTabsLessonDefaultsDraft(null);
      return {
        ...prev,
        [botId]: {
          ...existing,
          loaded: true,
          lessonDefaults: nextLessonDefaults,
          [field]: value,
        },
      };
    });
    if (field === 'officeId') {
      setTabsLessonOptionsByBot((prev) => ({
        ...prev,
        [botId]: null,
      }));
    }
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
  }, []);

  const handleTabsFeatureToggle = useCallback((botId, featureKey, enabled) => {
    if (!botId || !featureKey) return;
    setTabsConfigByBot((prev) => {
      const existing = prev[botId] || createTabsConfigDraft();
      return {
        ...prev,
        [botId]: {
          ...existing,
          features: {
            ...(existing.features || DEFAULT_TABS_FEATURES),
            [featureKey]: enabled === true,
          },
        },
      };
    });
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
  }, []);

  const handleTabsLessonOptionsLoad = useCallback(async (botId) => {
    if (!botId || !canEditBot) return;
    const current = tabsConfigByBot[botId] || createTabsConfigDraft();
    const officeIdRaw = String(current.officeId || '').trim();
    const officeIdValue = Number(officeIdRaw);
    if (!/^\d+$/.test(officeIdRaw) || !Number.isInteger(officeIdValue) || officeIdValue <= 0) {
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: 'Office ID må være et gyldig positivt heltall.',
      }));
      return;
    }

    const apiKey = String(current.apiKey || '').trim();
    setTabsLessonOptionsLoadingByBot((prev) => ({ ...prev, [botId]: true }));
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.getBotTabsLessonOptions(botId, {
        officeId: officeIdValue,
        ...(apiKey ? { apiKey } : {}),
      });
      const options = response?.options && typeof response.options === 'object'
        ? response.options
        : { categories: [], products: [] };
      setTabsLessonOptionsByBot((prev) => ({
        ...prev,
        [botId]: {
          ...options,
          officeId: response?.officeId ?? officeIdValue,
          mode: response?.mode || null,
          fetchedAt: new Date().toISOString(),
        },
      }));
      notifyBotSuccess(botId, response?.summary || 'Kjøretimevalg hentet fra TABS.');
    } catch (err) {
      const message = err.message || 'Kunne ikke hente kjøretimevalg fra TABS.';
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: message,
      }));
      notifyBotError(botId, message);
    } finally {
      setTabsLessonOptionsLoadingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, notifyBotError, notifyBotSuccess, tabsConfigByBot]);

  const handleTabsLessonCategoryChange = useCallback((botId, nextCategoryId) => {
    if (!botId) return;
    const categoryId = String(nextCategoryId || '').trim();
    const options = tabsLessonOptionsByBot[botId];
    const categories = Array.isArray(options?.categories) ? options.categories : [];
    const selectedCategory = categories.find((entry) => String(entry?.webCategoryId || '') === categoryId) || null;
    setTabsConfigByBot((prev) => {
      const existing = prev[botId] || createTabsConfigDraft();
      return {
        ...prev,
        [botId]: {
          ...existing,
          lessonDefaults: {
            ...(existing.lessonDefaults || createTabsLessonDefaultsDraft(null)),
            webCategoryId: categoryId,
            productId: '',
            categoryName: selectedCategory?.categoryName ? String(selectedCategory.categoryName) : '',
            productName: '',
            className: '',
          },
        },
      };
    });
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
  }, [tabsLessonOptionsByBot]);

  const handleTabsLessonProductChange = useCallback((botId, nextProductId) => {
    if (!botId) return;
    const productId = String(nextProductId || '').trim();
    const options = tabsLessonOptionsByBot[botId];
    const categories = Array.isArray(options?.categories) ? options.categories : [];
    setTabsConfigByBot((prev) => {
      const existing = prev[botId] || createTabsConfigDraft();
      const categoryId = String(existing.lessonDefaults?.webCategoryId || '').trim();
      const selectedCategory = categories.find((entry) => String(entry?.webCategoryId || '') === categoryId) || null;
      const products = Array.isArray(selectedCategory?.products) ? selectedCategory.products : [];
      const selectedProduct = products.find((entry) => String(entry?.productId || '') === productId) || null;
      return {
        ...prev,
        [botId]: {
          ...existing,
          lessonDefaults: {
            ...(existing.lessonDefaults || createTabsLessonDefaultsDraft(null)),
            webCategoryId: categoryId,
            productId,
            categoryName: selectedCategory?.categoryName ? String(selectedCategory.categoryName) : '',
            productName: selectedProduct?.productName ? String(selectedProduct.productName) : '',
            className: selectedProduct?.className ? String(selectedProduct.className) : '',
          },
        },
      };
    });
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
  }, [tabsLessonOptionsByBot]);

  const handleTabsConfigSave = useCallback(async (botId) => {
    if (!botId || !canEditBot) return;

    const current = tabsConfigByBot[botId] || createTabsConfigDraft();
    const officeIdRaw = String(current.officeId || '').trim();
    const officeIdValue = Number(officeIdRaw);
    if (!/^\d+$/.test(officeIdRaw) || !Number.isInteger(officeIdValue) || officeIdValue <= 0) {
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: 'Office ID må være et gyldig positivt heltall.',
      }));
      return;
    }

    const apiKey = String(current.apiKey || '').trim();
    const lessonDefaultsPayload = null;

    const payload = {
      officeId: officeIdValue,
      features: {
        courses: current.features?.courses !== false,
        prices: current.features?.prices !== false,
        booking: current.features?.booking !== false,
      },
      lessonDefaults: lessonDefaultsPayload,
      ...(apiKey ? { apiKey } : {}),
    };

    setTabsConfigSavingByBot((prev) => ({ ...prev, [botId]: true }));
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.updateBotTabsConfig(botId, payload);
      setTabsConfigByBot((prev) => ({
        ...prev,
        [botId]: createTabsConfigDraft(response || {
          configured: true,
          officeId: officeIdValue,
          apiKeyMasked: prev[botId]?.apiKeyMasked || null,
          features: payload.features,
          lessonDefaults: lessonDefaultsPayload,
          lastTestStatus: prev[botId]?.lastTestStatus || null,
          updatedAt: new Date().toISOString(),
        }),
      }));
      notifyBotSuccess(botId, 'TABS-oppsettet er lagret.');
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre TABS-oppsett.';
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: message,
      }));
      notifyBotError(botId, message);
    } finally {
      setTabsConfigSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, tabsConfigByBot, notifyBotError, notifyBotSuccess]);

  const handleTabsConfigTest = useCallback(async (botId) => {
    if (!botId || !canEditBot) return;

    const current = tabsConfigByBot[botId] || createTabsConfigDraft();
    const officeIdRaw = String(current.officeId || '').trim();
    const officeIdValue = Number(officeIdRaw);
    if (!/^\d+$/.test(officeIdRaw) || !Number.isInteger(officeIdValue) || officeIdValue <= 0) {
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: 'Office ID må være et gyldig positivt heltall.',
      }));
      return;
    }

    const apiKey = String(current.apiKey || '').trim();
    const payload = {
      officeId: officeIdValue,
      ...(apiKey ? { apiKey } : {}),
    };

    setTabsConfigTestingByBot((prev) => ({ ...prev, [botId]: true }));
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.testBotTabsConfig(botId, payload);
      setTabsConfigByBot((prev) => {
        const existing = prev[botId] || createTabsConfigDraft();
        return {
          ...prev,
          [botId]: {
            ...existing,
            lastTestStatus: response && typeof response === 'object' ? response : null,
          },
        };
      });

      const summary = response?.summary || 'Integrasjonstest fullført.';
      if (response?.ok === true) {
        notifyBotSuccess(botId, summary);
      } else {
        setTabsConfigErrorByBot((prev) => ({
          ...prev,
          [botId]: summary,
        }));
        notifyBotError(botId, summary);
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke teste TABS-oppsett.';
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: message,
      }));
      notifyBotError(botId, message);
    } finally {
      setTabsConfigTestingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, notifyBotError, notifyBotSuccess, tabsConfigByBot]);

  const handleTabsConfigDisconnect = useCallback(async (botId) => {
    if (!botId || !canEditBot) return;
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Koble fra TABS-oppsettet for denne boten?');
      if (!confirmed) return;
    }

    setTabsConfigSavingByBot((prev) => ({ ...prev, [botId]: true }));
    setTabsConfigErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      await DashboardApi.deleteBotTabsConfig(botId);
      setTabsConfigByBot((prev) => ({
        ...prev,
        [botId]: createTabsConfigDraft({
          configured: false,
          officeId: null,
          apiKeyMasked: null,
          lessonDefaults: null,
          updatedAt: null,
        }),
      }));
      setTabsLessonOptionsByBot((prev) => ({
        ...prev,
        [botId]: null,
      }));
      notifyBotSuccess(botId, 'TABS-oppsettet er koblet fra.');
    } catch (err) {
      const message = err.message || 'Kunne ikke koble fra TABS-oppsett.';
      setTabsConfigErrorByBot((prev) => ({
        ...prev,
        [botId]: message,
      }));
      notifyBotError(botId, message);
    } finally {
      setTabsConfigSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, notifyBotError, notifyBotSuccess]);

  // Knowledge base handlers
  const loadKnowledgeForBot = useCallback(async (botId, botExternalId) => {
    if (!botExternalId || knowledgeLoading[botId]) return;
    setKnowledgeLoading((prev) => ({ ...prev, [botId]: true }));
    setKnowledgeError((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.getBotKnowledge(null, botExternalId);
      const items = Array.isArray(response?.items)
        ? response.items.map(mapKnowledgeItem)
        : [];

      const rawMaxEntries = response?.maxKnowledgebaseEntries ?? response?.limit ?? null;
      const normalizedMaxEntries = rawMaxEntries === null || rawMaxEntries === undefined
        ? null
        : Number(rawMaxEntries);
      const maxEntries = Number.isFinite(normalizedMaxEntries) ? normalizedMaxEntries : null;

      setKnowledgePairs((prev) => ({ ...prev, [botId]: items }));
      setKnowledgeMaxEntries((prev) => ({ ...prev, [botId]: maxEntries }));
    } catch (err) {
      const message = err.message || 'Kunne ikke hente kunnskapsbase.';
      setKnowledgeError((prev) => ({ ...prev, [botId]: message }));
      notifyBotError(botId, message);
      setKnowledgePairs((prev) => ({ ...prev, [botId]: [] }));
      setKnowledgeMaxEntries((prev) => ({ ...prev, [botId]: null }));
    } finally {
      setKnowledgeLoading((prev) => ({ ...prev, [botId]: false }));
    }
  }, [knowledgeLoading, notifyBotError]);

  const handleKnowledgePairChange = (botId, pairId, field, value) => {
    if (!canEditBot) return;
    setKnowledgePairs((prev) => ({
      ...prev,
      [botId]: (prev[botId] || []).map((pair) =>
        pair.id === pairId && !pair.readOnly ? { ...pair, [field]: value } : pair
      ),
    }));
  };

  const handleKnowledgeRemovePair = (botId, pairId) => {
    if (!canEditBot) return;
    setKnowledgePairs((prev) => ({
      ...prev,
      [botId]: (prev[botId] || []).filter((pair) => pair.id !== pairId || pair.readOnly),
    }));
  };

  const handleKnowledgeAddPair = (botId) => {
    if (!canEditBot) return;
    setKnowledgePairs((prev) => ({
      ...prev,
      [botId]: [...(prev[botId] || []), createEmptyKnowledgePair()],
    }));
  };

  const handleKnowledgeSave = async (botId, botExternalId) => {
    if (!botExternalId || !canEditBot) return;
    const pairs = (knowledgePairs[botId] || []).filter((pair) => pair.sourceType !== 'website');
    // Sanitize: only non-empty pairs
    const sanitized = [];
    const seen = new Set();
    pairs.forEach((pair) => {
      const keyword = (pair.keyword || '').trim();
      const content = (pair.content || '').trim();
      if (keyword && content && !seen.has(keyword)) {
        seen.add(keyword);
        sanitized.push({ keyword, content });
      }
    });
    setKnowledgeSaving((prev) => ({ ...prev, [botId]: true }));
    setKnowledgeError((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.saveBotKnowledge(null, botExternalId, sanitized);

      const rawMaxEntries = response?.maxKnowledgebaseEntries ?? response?.limit ?? null;
      const normalizedMaxEntries = rawMaxEntries === null || rawMaxEntries === undefined
        ? null
        : Number(rawMaxEntries);
      const maxEntries = Number.isFinite(normalizedMaxEntries) ? normalizedMaxEntries : null;
      setKnowledgeMaxEntries((prev) => ({ ...prev, [botId]: maxEntries }));

      if (Array.isArray(response?.items)) {
        const mapped = response.items.map(mapKnowledgeItem);
        setKnowledgePairs((prev) => ({ ...prev, [botId]: mapped }));
      }
      notifyBotSuccess(botId, 'Kunnskapsbasen er oppdatert.');
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre kunnskapsbase.';
      setKnowledgeError((prev) => ({ ...prev, [botId]: message }));
      notifyBotError(botId, message);
    } finally {
      setKnowledgeSaving((prev) => ({ ...prev, [botId]: false }));
    }
  };

  const handleKnowledgeGenerate = async (botId, botExternalId, text) => {
    if (!botExternalId || !canEditBot || !text.trim()) return;
    setKnowledgeGenerateLoading((prev) => ({ ...prev, [botId]: true }));
    setKnowledgeError((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.generateBotKnowledge(null, botExternalId, text.trim());

      const rawMaxEntries = response?.maxKnowledgebaseEntries ?? response?.limit ?? null;
      const normalizedMaxEntries = rawMaxEntries === null || rawMaxEntries === undefined
        ? null
        : Number(rawMaxEntries);
      const maxEntries = Number.isFinite(normalizedMaxEntries) ? normalizedMaxEntries : null;
      setKnowledgeMaxEntries((prev) => ({ ...prev, [botId]: maxEntries }));

      if (Array.isArray(response?.items) && response.items.length > 0) {
        const mapped = response.items.map(mapKnowledgeItem);
        setKnowledgePairs((prev) => ({ ...prev, [botId]: mapped }));
        const generatedCount = Array.isArray(response?.generated) ? response.generated.length : 0;
        if (generatedCount > 0) {
          notifyBotSuccess(botId, `${generatedCount} nye kunnskapsinnslag ble generert og lagret.`);
        } else {
          notifyBotSuccess(botId, 'Kunnskapsbasen ble oppdatert.');
        }
      } else {
        notifyBotSuccess(botId, 'Ingen nye kunnskapsinnslag ble generert.');
      }
      setKnowledgeGenerateOpen((prev) => ({ ...prev, [botId]: false }));
      setKnowledgeGenerateText((prev) => ({ ...prev, [botId]: '' }));
    } catch (err) {
      const message = err.message || 'Kunne ikke generere kunnskapsinnslag.';
      setKnowledgeError((prev) => ({ ...prev, [botId]: message }));
      notifyBotError(botId, message);
    } finally {
      setKnowledgeGenerateLoading((prev) => ({ ...prev, [botId]: false }));
    }
  };

  const setProductInfoMessage = useCallback((botId, message, isError = false) => {
    if (isError) {
      setProductErrorByBot((prev) => ({ ...prev, [botId]: message }));
      notifyBotError(botId, message);
      return;
    }
    setProductErrorByBot((prev) => ({ ...prev, [botId]: null }));
    notifyBotSuccess(botId, message);
  }, [notifyBotError, notifyBotSuccess]);

  const loadProductsForBot = useCallback(async (botId, { force = false } = {}) => {
    if (!botId) return;
    if (!force && productLoadingByBot[botId]) return;
    if (!force && Array.isArray(productItemsByBot[botId]) && Array.isArray(productProposalsByBot[botId])) {
      return;
    }

    setProductLoadingByBot((prev) => ({ ...prev, [botId]: true }));
    setProductErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      const response = await DashboardApi.getBotProducts(botId);
      const items = Array.isArray(response?.items) ? response.items.map(mapProductItem) : [];
      const proposals = Array.isArray(response?.proposals) ? response.proposals.map(mapProductProposal) : [];
      setProductItemsByBot((prev) => ({ ...prev, [botId]: items }));
      setProductProposalsByBot((prev) => ({ ...prev, [botId]: proposals }));
      setProductSyncByBot((prev) => ({ ...prev, [botId]: response?.sync || null }));
    } catch (err) {
      const message = err.message || 'Kunne ikke hente produkter.';
      setProductErrorByBot((prev) => ({ ...prev, [botId]: message }));
      notifyBotError(botId, message);
      setProductItemsByBot((prev) => ({ ...prev, [botId]: [] }));
      setProductProposalsByBot((prev) => ({ ...prev, [botId]: [] }));
      setProductSyncByBot((prev) => ({ ...prev, [botId]: null }));
    } finally {
      setProductLoadingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [productItemsByBot, productLoadingByBot, productProposalsByBot, notifyBotError]);

  const openProductCreateModal = useCallback((botId) => {
    if (!botId || !canEditBot) return;
    setProductModalState({
      botId,
      mode: 'create',
      productId: null,
      draft: createProductModalDraft(),
    });
  }, [canEditBot]);

  const openProductEditModal = useCallback((botId, item) => {
    if (!botId || !item?.id || !canEditBot) return;
    setProductModalState({
      botId,
      mode: 'edit',
      productId: item.id,
      draft: createProductModalDraft(item),
    });
  }, [canEditBot]);

  const closeProductModal = useCallback(() => {
    setProductModalState(null);
  }, []);

  const handleProductModalFieldChange = useCallback((field, value) => {
    setProductModalState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        draft: {
          ...prev.draft,
          [field]: value,
        },
      };
    });
  }, []);

  const handleProductModalNotificationChange = useCallback((field, value) => {
    if (!canEditBot) return;
    setProductModalState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        draft: {
          ...prev.draft,
          notificationSettings: {
            ...createEmptyProductNotificationSettings(),
            ...(prev.draft?.notificationSettings || {}),
            [field]: value,
          },
        },
      };
    });
  }, [canEditBot]);

  const handleProductModalInboxConfigAddField = useCallback(() => {
    if (!canEditBot) return;
    setProductModalState((prev) => {
      if (!prev) return prev;
      const currentConfig = cloneProductInboxConfig(prev.draft?.inboxConfig);
      return {
        ...prev,
        draft: {
          ...prev.draft,
          inboxConfig: {
            ...currentConfig,
            fields: [...currentConfig.fields, createEmptyProductInboxField()],
          },
        },
      };
    });
  }, [canEditBot]);

  const handleProductModalInboxConfigFieldChange = useCallback((fieldIndex, property, value) => {
    if (!canEditBot) return;
    setProductModalState((prev) => {
      if (!prev) return prev;
      const currentConfig = cloneProductInboxConfig(prev.draft?.inboxConfig);
      return {
        ...prev,
        draft: {
          ...prev.draft,
          inboxConfig: {
            ...currentConfig,
            fields: currentConfig.fields.map((field, index) => {
              if (index !== fieldIndex) return field;
              const nextField = { ...field, [property]: value };
              if (property === 'type' && value !== 'select') {
                nextField.options = [];
              }
              return nextField;
            }),
          },
        },
      };
    });
  }, [canEditBot]);

  const handleProductModalInboxConfigRemoveField = useCallback((fieldIndex) => {
    if (!canEditBot) return;
    setProductModalState((prev) => {
      if (!prev) return prev;
      const currentConfig = cloneProductInboxConfig(prev.draft?.inboxConfig);
      return {
        ...prev,
        draft: {
          ...prev.draft,
          inboxConfig: {
            ...currentConfig,
            fields: currentConfig.fields.filter((_field, index) => index !== fieldIndex),
          },
        },
      };
    });
  }, [canEditBot]);

  const handleProductModalInboxConfigMoveField = useCallback((fieldIndex, direction) => {
    if (!canEditBot) return;
    setProductModalState((prev) => {
      if (!prev) return prev;
      const currentConfig = cloneProductInboxConfig(prev.draft?.inboxConfig);
      const fields = [...currentConfig.fields];
      const nextIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
      if (fieldIndex < 0 || fieldIndex >= fields.length || nextIndex < 0 || nextIndex >= fields.length) {
        return prev;
      }
      const [field] = fields.splice(fieldIndex, 1);
      fields.splice(nextIndex, 0, field);
      return {
        ...prev,
        draft: {
          ...prev.draft,
          inboxConfig: {
            ...currentConfig,
            fields,
          },
        },
      };
    });
  }, [canEditBot]);

  const saveProductInboxConfig = useCallback(async (botId, productId, inboxConfig, notificationSettings, fallbackItem = null) => {
    const response = await DashboardApi.updateBotProductInboxConfig(
      botId,
      productId,
      {
        ...buildProductInboxConfigPayload(inboxConfig),
        notificationSettings: buildProductNotificationSettingsPayload(notificationSettings),
      },
    );
    return mapProductItem(response?.item || fallbackItem || {});
  }, []);

  const toggleProductProposalExpanded = useCallback((botId, proposalId) => {
    if (!botId || !proposalId) return;
    setProductProposalExpandedIdByBot((prev) => ({
      ...prev,
      [botId]: prev[botId] === proposalId ? null : proposalId,
    }));
  }, []);

  const handleProductModalSave = useCallback(async () => {
    if (!productModalState?.botId || !canEditBot) return;

    const { botId, mode, productId, draft } = productModalState;
    const payload = buildProductPayload(draft);
    if (!payload.name) {
      setProductInfoMessage(botId, 'Produktnavn er påkrevd.', true);
      return;
    }
    const configValidationError = validateProductInboxConfigDraft(draft.inboxConfig);
    if (configValidationError) {
      setProductInfoMessage(botId, configValidationError, true);
      return;
    }
    const notificationValidationError = validateProductNotificationSettingsDraft(draft.notificationSettings);
    if (notificationValidationError) {
      setProductInfoMessage(botId, notificationValidationError, true);
      return;
    }

    setProductSavingByBot((prev) => ({ ...prev, [botId]: true }));
    setProductErrorByBot((prev) => ({ ...prev, [botId]: null }));
    try {
      let updatedItem;
      if (mode === 'create') {
        const response = await DashboardApi.createBotProduct(botId, payload);
        const created = mapProductItem(response?.item || payload);
        updatedItem = await saveProductInboxConfig(botId, created.id, draft.inboxConfig, draft.notificationSettings, created);
        setProductItemsByBot((prev) => ({
          ...prev,
          [botId]: [updatedItem, ...(prev[botId] || [])],
        }));
        setProductInfoMessage(botId, 'Produktet er opprettet.');
      } else {
        const response = await DashboardApi.updateBotProduct(botId, productId, payload);
        const updated = mapProductItem(response?.item || draft);
        updatedItem = await saveProductInboxConfig(botId, updated.id, draft.inboxConfig, draft.notificationSettings, updated);
        setProductItemsByBot((prev) => ({
          ...prev,
          [botId]: (prev[botId] || []).map((entry) => (entry.id === updatedItem.id ? updatedItem : entry)),
        }));
        setProductInfoMessage(botId, 'Produktet er lagret.');
      }
      setProductModalState(null);
    } catch (err) {
      setProductInfoMessage(
        botId,
        err.message || (mode === 'create' ? 'Kunne ikke opprette produkt.' : 'Kunne ikke lagre produkt.'),
        true,
      );
    } finally {
      setProductSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, productModalState, saveProductInboxConfig, setProductInfoMessage]);

  const handleProductArchiveToggle = useCallback(async (botId, item) => {
    if (!botId || !item?.id || !canEditBot) return null;
    setProductSavingByBot((prev) => ({ ...prev, [botId]: true }));
    try {
      const response = item.status === 'archived'
        ? await DashboardApi.restoreBotProduct(botId, item.id)
        : await DashboardApi.archiveBotProduct(botId, item.id);
      const updated = mapProductItem(response?.item || item);
      setProductItemsByBot((prev) => ({
        ...prev,
        [botId]: (prev[botId] || []).map((entry) => (entry.id === updated.id ? updated : entry)),
      }));
      setProductInfoMessage(
        botId,
        updated.status === 'archived' ? 'Produktet er arkivert.' : 'Produktet er aktivert.',
      );
      return updated;
    } catch (err) {
      setProductInfoMessage(botId, err.message || 'Kunne ikke oppdatere produktstatus.', true);
      return null;
    } finally {
      setProductSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, setProductInfoMessage]);

  const handleProductModalArchive = useCallback(async () => {
    if (!productModalState?.botId || productModalState.mode !== 'edit') return;
    const botId = productModalState.botId;
    const item = productItemsByBot[botId]?.find((entry) => entry.id === productModalState.productId);
    if (!item) return;
    const updated = await handleProductArchiveToggle(botId, item);
    if (updated?.status === 'archived') {
      setProductModalState(null);
    }
  }, [handleProductArchiveToggle, productItemsByBot, productModalState]);

  const handleProductProposalApprove = useCallback(async (botId, proposalId) => {
    if (!botId || !proposalId || !canEditBot) return;
    setProductSavingByBot((prev) => ({ ...prev, [botId]: true }));
    try {
      await DashboardApi.approveBotProductProposal(botId, proposalId, {});
      await loadProductsForBot(botId, { force: true });
      setProductInfoMessage(botId, 'Forslaget er godkjent.');
    } catch (err) {
      setProductInfoMessage(botId, err.message || 'Kunne ikke godkjenne forslag.', true);
    } finally {
      setProductSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, loadProductsForBot, setProductInfoMessage]);

  const handleProductProposalReject = useCallback(async (botId, proposalId) => {
    if (!botId || !proposalId || !canEditBot) return;
    setProductSavingByBot((prev) => ({ ...prev, [botId]: true }));
    try {
      await DashboardApi.rejectBotProductProposal(botId, proposalId, {});
      await loadProductsForBot(botId, { force: true });
      setProductInfoMessage(botId, 'Forslaget er avvist.');
    } catch (err) {
      setProductInfoMessage(botId, err.message || 'Kunne ikke avvise forslag.', true);
    } finally {
      setProductSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, loadProductsForBot, setProductInfoMessage]);

  const handleProductBulkAction = useCallback(async (botId, action) => {
    if (!botId || !action || !canEditBot) return;
    const proposals = (productProposalsByBot[botId] || []).filter((entry) => entry.status === 'pending');
    const proposalIds = proposals.map((entry) => entry.id).filter(Boolean);
    if (proposalIds.length === 0) return;
    setProductSavingByBot((prev) => ({ ...prev, [botId]: true }));
    try {
      await DashboardApi.bulkUpdateBotProductProposals(botId, { action, proposalIds });
      await loadProductsForBot(botId, { force: true });
      setProductInfoMessage(
        botId,
        action === 'approve'
          ? `${proposalIds.length} forslag er godkjent.`
          : `${proposalIds.length} forslag er avvist.`,
      );
    } catch (err) {
      setProductInfoMessage(botId, err.message || 'Kunne ikke oppdatere forslag i bulk.', true);
    } finally {
      setProductSavingByBot((prev) => ({ ...prev, [botId]: false }));
    }
  }, [canEditBot, loadProductsForBot, productProposalsByBot, setProductInfoMessage]);

  const renderBotSection = (sectionId, bot) => {
    switch (sectionId) {
      case 'overview': {
        const statusPayload = websiteScanStatus[bot.id] || null;
        const loadingScan = Boolean(websiteScanLoading[bot.id]);
        const scanRequesting = Boolean(websiteScanRequesting[bot.id]);
        const scanErr = websiteScanError[bot.id];
        const installStatusKey = statusPayload?.installation?.status || ((bot.websiteUrl || '').trim() ? 'not_verified' : 'unconfigured');
        const installStatusMeta = WEBSITE_INSTALL_STATUS_META[installStatusKey] || WEBSITE_INSTALL_STATUS_META.not_verified;
        const scanData = statusPayload?.scan || {};
        const scanGuardrail = scanData.guardrail || null;
        const activeJob = scanData.activeJob || null;
        const activeJobState = activeJob?.status || null;
        const events = Array.isArray(scanData.events) ? scanData.events.slice(-10) : [];
        const requestsRemaining = Number(scanData.requestsRemaining ?? 3);
        const requestLimit = Number(scanData.requestLimit ?? 3) || 3;
        const metrics = scanData.metrics || null;
        const progressDiscovered = Number(activeJob?.pages_discovered ?? metrics?.pagesDiscovered ?? 0);
        const progressCrawled = Number(activeJob?.pages_crawled ?? metrics?.pagesCrawled ?? 0);
        const latestJobState = String(scanData.latestJob?.status || '').toLowerCase();
        const hasTerminalJob = ['completed', 'failed', 'canceled'].includes(latestJobState);
        const progressTotal = hasTerminalJob && !WEBSITE_SCAN_ACTIVE_STATES.has(activeJobState)
          ? Math.max(progressCrawled, Number(metrics?.pagesCrawled || 0))
          : progressDiscovered;
        const progressPercent = progressTotal > 0
          ? Math.min(100, Math.round((progressCrawled / progressTotal) * 100))
          : (activeJobState === 'running' ? 8 : 0);
        const scanAutomationEnabled = typeof bot.websiteScanEnabled === 'boolean'
          ? bot.websiteScanEnabled
          : scanData.automaticEnabled !== false;
        const scanEligible = typeof scanGuardrail?.eligible === 'boolean'
          ? scanGuardrail.eligible
          : installStatusKey === 'verified';
        const scanGuardrailMessage = scanGuardrail?.message || null;
        const scanBypassEnabled = isSuperAdmin;
        const scanBlockedByVerification = !scanBypassEnabled && !scanEligible;
        const scanLimitReached = !scanBypassEnabled && requestsRemaining <= 0;
        const canToggleAutomaticScan = canEditBot && !WEBSITE_SCAN_ACTIVE_STATES.has(activeJobState);
        const canRequestScan = canEditBot
          && !scanRequesting
          && Boolean((bot.websiteUrl || '').trim())
          && !scanLimitReached
          && !scanBlockedByVerification
          && !WEBSITE_SCAN_ACTIVE_STATES.has(activeJobState);
        const requestScanTitle = !Boolean((bot.websiteUrl || '').trim())
          ? 'Legg til en nettside i Oversikt før du starter nettsideskann.'
          : scanBlockedByVerification
            ? (scanGuardrailMessage || 'Nettsiden må verifiseres før skann kan startes.')
            : scanLimitReached
              ? 'Du har brukt opp manuelle nettsideskann i inneværende periode.'
              : undefined;

        return (
          <section className="bot-section overview-section">
            <header className="bot-section-header">
              <h3>Oversikt</h3>
              <p>Administrer navn, segment, nettsted og automatisert webscan.</p>
            </header>
            <div className="bot-grid bot-grid--three">
              <label>
                Bot-navn
                <input
                  className="input"
                  type="text"
                  value={bot.name}
                  onChange={(event) => handleConfigChange(bot.id, 'name', event.target.value)}
                />
              </label>
              <label>
                Segment
                <input
                  className="input"
                  type="text"
                  value={bot.segment || ''}
                  onChange={(event) => handleConfigChange(bot.id, 'segment', event.target.value)}
                />
              </label>
              <label>
                Bot-ID
                <input className="input" type="text" value={bot.externalId || 'Genereres automatisk'} disabled />
                <span className="input-hint">Brukes til embed og forhåndsvisning.</span>
              </label>
            </div>
            <div className="bot-grid bot-grid--two">
              <label className="website-url-field">
                Primær nettside
                <input
                  className="input"
                  type="url"
                  value={bot.websiteUrl || ''}
                  placeholder="https://dittdomene.no"
                  onChange={(event) => handleConfigChange(bot.id, 'websiteUrl', event.target.value)}
                />
                <span className="input-hint">Brukes for installasjonskontroll og månedlig webscan.</span>
              </label>
              <div className="website-install-state">
                <div className="website-install-head">
                  <span className={`website-install-pill ${installStatusMeta.className}`}>
                    {installStatusMeta.label}
                  </span>
                  <button
                    type="button"
                    className="button button-secondary website-install-refresh"
                    onClick={() => handleVerifyWebsiteInstallation(bot.id)}
                    disabled={loadingScan}
                  >
                    {loadingScan ? 'Sjekker…' : 'Sjekk nettside'}
                  </button>
                </div>
                <div className="website-install-meta">
                  <span>Siste godkjente check-in: {formatDateTime(statusPayload?.installation?.lastSeenAllowedAt)}</span>
                  {statusPayload?.website?.origin && (
                    <span>Origin: {statusPayload.website.origin}</span>
                  )}
                </div>
              </div>
            </div>

            <div className={`website-scan-card ${WEBSITE_SCAN_ACTIVE_STATES.has(activeJobState) ? 'is-active' : ''}`}>
              <div className="website-scan-card-head">
                <div>
                  <h4>Automatisk nettsideskann</h4>
                  <p className="input-hint">
                    {scanBypassEnabled
                      ? 'Superadmin: manuelle nettsideskann er ikke begrenset av verifisering eller kvote.'
                      : `Manuelle forespørsler igjen: ${requestsRemaining} / ${requestLimit}`}
                  </p>
                  <div className="website-scan-auto-control">
                    <button
                      type="button"
                      className={`bot-status-toggle ${scanAutomationEnabled ? 'is-active' : 'is-inactive'}`}
                      onClick={() => handleConfigChange(bot.id, 'websiteScanEnabled', !scanAutomationEnabled)}
                      disabled={!canToggleAutomaticScan}
                      role="switch"
                      aria-checked={scanAutomationEnabled}
                      title="Aktiver/deaktiver automatisk nettsideskann"
                    >
                      <span className="bot-status-track">
                        <span className="bot-status-thumb" />
                      </span>
                      <span className="bot-status-text">{scanAutomationEnabled ? 'Aktiv' : 'Deaktivert'}</span>
                    </button>
                    <span className="input-hint">
                      {scanAutomationEnabled
                        ? 'Automatisk månedlig skann er aktiv.'
                        : 'Automatisk skann er av. Eksisterende webscan-data beholdes, men brukes ikke før funksjonen aktiveres.'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => handleRequestWebsiteScan(bot.id)}
                  disabled={!canRequestScan}
                  title={requestScanTitle}
                >
                  {scanRequesting ? 'Starter…' : 'Be om skann'}
                </button>
              </div>

              {scanBlockedByVerification && Boolean((bot.websiteUrl || '').trim()) && (
                <div className="inline-alert inline-alert-warning">
                  <strong>Verifisering kreves:</strong> {scanGuardrailMessage || 'Chatboten må verifiseres på nettstedet før skann kan startes.'}
                </div>
              )}

              {scanErr && (
                <div className="inline-alert inline-alert-error">
                  <strong>Feil:</strong> {scanErr}
                </div>
              )}

              <div className="website-scan-metrics">
                <div className="website-scan-stat">
                  <span>Neste månedlige skann</span>
                  <strong>
                    {scanAutomationEnabled
                      ? formatDateTime(scanData.nextRunAt || bot.websiteScanNextRunAt)
                      : 'Deaktivert'}
                  </strong>
                </div>
                <div className="website-scan-stat">
                  <span>Siste vellykkede skann</span>
                  <strong>{formatDateTime(scanData.lastSuccessAt || bot.websiteScanLastSuccessAt)}</strong>
                </div>
                <div className="website-scan-stat">
                  <span>Sider skannet</span>
                  <strong>{Number(metrics?.pagesCrawled || 0)}</strong>
                </div>
                <div className="website-scan-stat">
                  <span>Chunker indeksert</span>
                  <strong>{Number(metrics?.entriesIndexed ?? metrics?.pagesIndexed ?? 0)}</strong>
                </div>
                <div className="website-scan-stat">
                  <span>Hoppet over (grense)</span>
                  <strong>{Number(metrics?.pagesSkippedLimit || 0)}</strong>
                </div>
                <div className="website-scan-stat">
                  <span>Filtrert støy</span>
                  <strong>
                    {Number(metrics?.pagesSkippedBoilerplate || 0)
                      + Number(metrics?.pagesSkippedLowValue || 0)
                      + Number(metrics?.pagesSkippedDuplicate || 0)}
                  </strong>
                </div>
              </div>

              <div className="website-scan-progress">
                <div className="website-scan-progress-track">
                  <div
                    className={`website-scan-progress-value ${activeJobState === 'running' ? 'is-running' : ''}`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="input-hint">
                  {loadingScan ? 'Henter status…' : `${progressCrawled} / ${progressTotal || '—'} sider`}
                </span>
              </div>

              <div
                className="website-scan-timeline"
                ref={(node) => {
                  if (node) {
                    websiteScanTimelineRefs.current[bot.id] = node;
                  } else {
                    delete websiteScanTimelineRefs.current[bot.id];
                  }
                }}
              >
                {events.length === 0 ? (
                  <p className="input-hint">Ingen skannehistorikk enda.</p>
                ) : (
                  events.map((event) => {
                    const eventType = event?.event_type || 'info';
                    const label = WEBSITE_SCAN_EVENT_LABELS[eventType] || eventType;
                    return (
                      <div key={event.id || `${event.created_at}-${eventType}`} className="website-scan-event">
                        <span className="website-scan-event-type">{label}</span>
                        <span className="website-scan-event-text">{event.message}</span>
                        <span className="website-scan-event-time">{formatDateTime(event.created_at)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>
        );
      }
      case 'chat-builder':
        return (
          <section className="bot-section">
            <header className="bot-section-header">
              <h3>Design</h3>
              <p>
                Bygg en mer dynamisk ChatUI-konfigurasjon (header, layout, tokens).
                Dette lagres sammen med boten når du trykker <code>Lagre endringer</code>.
              </p>
            </header>
            <ChatBuilderEditor
              bot={bot}
              onBotConfigChange={handleConfigChange}
              onMenuLinkAdd={handleMenuLinkAdd}
              onMenuLinkChange={handleMenuLinkChange}
              onMenuLinkRemove={handleMenuLinkRemove}
              themePresets={themePresets}
              themePresetsLoading={themePresetsLoading}
              onThemePresetSelect={handleThemePresetSelect}
              logoUploadState={logoUploadState}
              onLogoUpload={handleLogoUpload}
              launcherIconUploadState={launcherIconUploadState}
              onLauncherIconUpload={handleLauncherIconUpload}
            />
          </section>
        );
      case 'behavior': {
        const proactiveEnabled = Boolean(String(bot.proactiveMessage || '').trim());
        const openChatEnabled = bot.proactiveOpenChat === true;
        const handleProactiveToggle = (enabled) => {
          if (!enabled) {
            handleConfigChange(bot.id, 'proactiveMessage', '');
            return;
          }
          handleConfigChange(bot.id, 'proactiveMessage', bot.proactiveMessage || DEFAULT_PROACTIVE_MESSAGE);
          if (bot.proactiveDelaySeconds === null || bot.proactiveDelaySeconds === undefined || bot.proactiveDelaySeconds === '') {
            handleConfigChange(bot.id, 'proactiveDelaySeconds', '10');
          }
          if (
            bot.proactiveCooldownSeconds === null
            || bot.proactiveCooldownSeconds === undefined
            || bot.proactiveCooldownSeconds === ''
          ) {
            handleConfigChange(bot.id, 'proactiveCooldownSeconds', String(DEFAULT_PROACTIVE_COOLDOWN_SECONDS));
          }
        };
        const handleOpenChatToggle = (enabled) => {
          handleConfigChange(bot.id, 'proactiveOpenChat', enabled);
          if (
            enabled
            && (bot.proactiveOpenChatDelaySeconds === null
              || bot.proactiveOpenChatDelaySeconds === undefined
              || bot.proactiveOpenChatDelaySeconds === '')
          ) {
            handleConfigChange(bot.id, 'proactiveOpenChatDelaySeconds', '10');
          }
        };

        return (
          <section className="bot-section">
            <header className="bot-section-header">
              <h3>Adferd</h3>
              <p>Styr dialog, tone og hva boten skal prioritere når flere handlinger er mulige.</p>
            </header>
            <div className="bot-grid bot-grid--two">
              <label>
                Velkomstmelding
                <textarea
                  className="textarea"
                  rows={3}
                  value={bot.welcomeMessage}
                  onChange={(event) => handleConfigChange(bot.id, 'welcomeMessage', event.target.value)}
                />
              </label>
              <label>
                Systeminstruksjoner
                <textarea
                  className="textarea"
                  rows={3}
                  value={bot.systemInstruction}
                  onChange={(event) => handleConfigChange(bot.id, 'systemInstruction', event.target.value)}
                />
              </label>
              <label>
                Adferd og tone
                <textarea
                  className="textarea"
                  rows={3}
                  value={bot.behavior}
                  onChange={(event) => handleConfigChange(bot.id, 'behavior', event.target.value)}
                />
                <span className="input-hint">Beskriv hvordan boten skal fremstå for kundene.</span>
              </label>
            </div>
            <div className="proactive-settings">
              <div className="proactive-columns">
                <div className="proactive-column">
                  <label className="toggle-field proactive-toggle">
                    <input
                      type="checkbox"
                      checked={proactiveEnabled}
                      onChange={(event) => handleProactiveToggle(event.target.checked)}
                      disabled={!canEditBot}
                    />
                    <span className="toggle-content">
                      <span className="toggle-label">Proaktiv</span>
                      <span className="input-hint">Vis en automatisk åpningsmelding etter en valgt forsinkelse.</span>
                    </span>
                  </label>
                  {proactiveEnabled && (
                    <div className="proactive-fields bot-grid bot-grid--full">
                      <label>
                        Proaktiv melding
                        <textarea
                          className="textarea"
                          rows={3}
                          value={bot.proactiveMessage}
                          onChange={(event) => handleConfigChange(bot.id, 'proactiveMessage', event.target.value)}
                          placeholder={DEFAULT_PROACTIVE_MESSAGE}
                          disabled={!canEditBot}
                        />
                      </label>
                      <label>
                        Forsinkelse (sekunder)
                        <input
                          className="input"
                          type="number"
                          min={0}
                          max={86400}
                          step={1}
                          value={bot.proactiveDelaySeconds ?? '0'}
                          onChange={(event) => handleConfigChange(bot.id, 'proactiveDelaySeconds', event.target.value)}
                          disabled={!canEditBot}
                        />
                        <span className="input-hint">
                          Hvor lenge boten venter før meldingen vises.
                        </span>
                      </label>
                      <label>
                        Vis igjen etter (sekunder)
                        <input
                          className="input"
                          type="number"
                          min={0}
                          max={86400}
                          step={1}
                          value={bot.proactiveCooldownSeconds ?? String(DEFAULT_PROACTIVE_COOLDOWN_SECONDS)}
                          onChange={(event) => handleConfigChange(bot.id, 'proactiveCooldownSeconds', event.target.value)}
                          disabled={!canEditBot}
                        />
                        <span className="input-hint">
                          Ventetid før meldingen kan vises igjen. Bruk 0 for ingen cooldown.
                        </span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="proactive-column">
                  <label className="toggle-field proactive-toggle">
                    <input
                      type="checkbox"
                      checked={openChatEnabled}
                      onChange={(event) => handleOpenChatToggle(event.target.checked)}
                      disabled={!canEditBot}
                    />
                    <span className="toggle-content">
                      <span className="toggle-label">Åpne chat</span>
                      <span className="input-hint">Åpne chatten automatisk etter en valgt forsinkelse.</span>
                    </span>
                  </label>
                  {openChatEnabled && (
                    <div className="proactive-fields bot-grid bot-grid--full">
                      <label>
                        Åpne etter (sekunder)
                        <input
                          className="input"
                          type="number"
                          min={0}
                          max={86400}
                          step={1}
                          value={bot.proactiveOpenChatDelaySeconds ?? '10'}
                          onChange={(event) => handleConfigChange(bot.id, 'proactiveOpenChatDelaySeconds', event.target.value)}
                          disabled={!canEditBot}
                        />
                        <span className="input-hint">
                          Hvor lenge boten venter før chatten åpnes automatisk.
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <ConversationPolicyPanel
              policy={bot.conversationPolicy || bot.conversation_policy}
              onChange={(nextPolicy) => handleConfigChange(bot.id, 'conversationPolicy', nextPolicy)}
              disabled={!canEditBot}
            />
          </section>
        );
      }
      case 'booking': {
        const bookingValue =
          bot.bookingScheduleId === null || bot.bookingScheduleId === undefined
            ? ''
            : String(bot.bookingScheduleId);
        return (
          <section className="bot-section">
            <header className="bot-section-header">
              <h3>Booking</h3>
              <p>Koble boten til en bookingplan så den kan foreslå ledige tider i samtaler.</p>
            </header>
            <div className="bot-grid bot-grid--two">
              <label>
                Bookingplan
                <select
                  className="select"
                  value={bookingValue}
                  onChange={(event) => handleBookingScheduleChange(bot.id, event.target.value)}
                  disabled={bookingSchedulesLoading}
                >
                  <option value="">Ingen valgt</option>
                  {bookingScheduleOptions.map((schedule) => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </option>
                  ))}
                </select>
                {bookingSchedulesLoading && <span className="input-hint">Laster bookingplaner…</span>}
                {bookingSchedulesError && <span className="input-hint error">{bookingSchedulesError}</span>}
                {!bookingSchedulesLoading && bookingScheduleOptions.length === 0 && !bookingSchedulesError && (
                  <span className="input-hint">
                    Ingen bookingplaner funnet. Opprett en plan under «Booking» før du knytter den til boten.
                  </span>
                )}
              </label>
              <div className="card bot-booking-summary">
                <div>
                  <h4 className="card-title">Administrer bookingplaner</h4>
                  <p className="card-subtitle">
                    Endre tilgjengelighet, varighet og deltakere fra booking-siden.
                  </p>
                </div>
                <a className="button button-secondary" href="/booking" target="_blank" rel="noopener noreferrer">
                  Åpne booking
                </a>
              </div>
            </div>
          </section>
        );
      }
      case 'integrations':
        return (
          <section className="bot-section bot-integrations">
            <header className="bot-section-header">
              <h3>Integrasjoner</h3>
              <p>Den gamle integrasjonsadministrasjonen er fjernet fra dashboardet.</p>
            </header>
            <p className="input-hint">
              Hvis denne boten trenger nye eksterne koblinger senere, må det bygges som en ny ikke-MCP funksjon.
            </p>
          </section>
        );
      case 'tools': {
        const availableTools = mcpTools;

        return (
          <section className="bot-section bot-tools-section">
            <header className="bot-section-header">
              <h3>Verktøy</h3>
              <p>Disse verktøyene er tilgjengelige for boten basert på aktive tillegg og botens runtime-kontekst.</p>
            </header>

            {mcpToolsLoading ? (
              <p className="input-hint">Laster verktøy…</p>
            ) : mcpToolsError ? (
              <p className="input-hint error">⚠️ {mcpToolsError}</p>
            ) : availableTools.length === 0 ? (
              <div className="bot-tools-empty">
                <p>Ingen verktøy er tilgjengelige for denne boten akkurat nå.</p>
                <p className="input-hint">
                  Gå til <Link to="/selskap/tilleggsverktoy">Tilleggsverktøy</Link> for å aktivere flere funksjoner.
                </p>
              </div>
            ) : (
              <div className="bot-tools-list">
                <h4 className="bot-tools-list-title">Tilgjengelige verktøy</h4>
                <p className="input-hint">
                  Verktøytilgang styres automatisk. Denne listen viser hva boten faktisk kan bruke akkurat nå.
                </p>
                <div className="bot-tools-grid">
                  {availableTools.map((tool) => {
                    const isDrivingSchoolBot = false;
                    const showTabsSetup = tool.name === TABS_TOOL_NAME;
                    const tabsConfig = tabsConfigByBot[bot.id] || null;
                    const tabsLoading = Boolean(tabsConfigLoadingByBot[bot.id]);
                    const tabsSaving = Boolean(tabsConfigSavingByBot[bot.id]);
                    const tabsTesting = Boolean(tabsConfigTestingByBot[bot.id]);
                    const tabsLessonOptionsLoading = Boolean(tabsLessonOptionsLoadingByBot[bot.id]);
                    const tabsLessonOptions = tabsLessonOptionsByBot[bot.id] || null;
                    const tabsError = tabsConfigErrorByBot[bot.id] || null;
                    const tabsBusy = tabsLoading || tabsSaving || tabsTesting || tabsLessonOptionsLoading || !canEditBot;
                    const tabsUpdatedAt = tabsConfig?.updatedAt
                      ? new Date(tabsConfig.updatedAt).toLocaleString('no-NO')
                      : null;
                    const lessonCategories = Array.isArray(tabsLessonOptions?.categories)
                      ? tabsLessonOptions.categories
                      : [];
                    const selectedLessonCategoryId = String(tabsConfig?.lessonDefaults?.webCategoryId || '').trim();
                    const selectedLessonCategory = lessonCategories.find(
                      (entry) => String(entry?.webCategoryId || '') === selectedLessonCategoryId,
                    ) || null;
                    const lessonProducts = Array.isArray(selectedLessonCategory?.products)
                      ? selectedLessonCategory.products
                      : [];
                    const selectedLessonProductId = String(tabsConfig?.lessonDefaults?.productId || '').trim();
                    const tabsLastTest = tabsConfig?.lastTestStatus && typeof tabsConfig.lastTestStatus === 'object'
                      ? tabsConfig.lastTestStatus
                      : null;
                    const tabsTestMode = tabsLastTest?.mode || null;
                    const tabsTestModeLabel = tabsTestMode === 'ip_whitelist'
                      ? 'IP whitelist'
                      : tabsTestMode === 'api_key'
                        ? 'API key'
                        : tabsTestMode === 'failed'
                          ? 'Feilet'
                          : null;

                    return (
                      <div key={tool.name} className="bot-tool-card is-enabled">
                        <div className="bot-tool-info">
                          <span className="bot-tool-name">{tool.displayName || tool.name}</span>
                          {tool.description && (
                            <span className="bot-tool-description">{tool.description}</span>
                          )}
                          {tool.riskLevel && (
                            <span className="bot-tool-bundle">Risikonivå: {tool.riskLevel}</span>
                          )}
                        </div>
                        {showTabsSetup && (
                              <div className="tabs-tool-config-panel">
                                <div className="tabs-tool-config-header">
                                  <h5>TABS-oppsett</h5>
                                  {tabsConfig?.apiKeyMasked && (
                                    <span className="tabs-tool-config-pill">{tabsConfig.apiKeyMasked}</span>
                                  )}
                                </div>
                                <p className="input-hint">
                                  For tilgang må dere enten legge inn gyldig API key eller whitelist IP <code>172.238.102.31</code> hos TABS.
                                </p>
                                {tabsLoading && !tabsConfig?.loaded ? (
                                  <p className="input-hint">Laster TABS-oppsett…</p>
                                ) : (
                                  <>
                                    <label className="tabs-tool-config-field">
                                      <span>Office ID</span>
                                      <input
                                        type="text"
                                        className="input"
                                        value={tabsConfig?.officeId ?? ''}
                                        onChange={(event) =>
                                          handleTabsConfigFieldChange(bot.id, 'officeId', event.target.value)
                                        }
                                        placeholder="F.eks. 12345"
                                        inputMode="numeric"
                                        disabled={tabsBusy}
                                      />
                                    </label>
                                    <label className="tabs-tool-config-field">
                                      <span>API key (valgfri)</span>
                                      <input
                                        type="password"
                                        className="input"
                                        value={tabsConfig?.apiKey ?? ''}
                                        onChange={(event) =>
                                          handleTabsConfigFieldChange(bot.id, 'apiKey', event.target.value)
                                        }
                                        placeholder={tabsConfig?.configured ? 'Ny API key (valgfritt)' : 'TABS API key (valgfri)'}
                                        autoComplete="new-password"
                                        disabled={tabsBusy}
                                      />
                                    </label>
                                    <div className="tabs-tool-feature-pills" role="group" aria-label="TABS-funksjoner">
                                      <button
                                        type="button"
                                        className={`tabs-tool-feature-pill${tabsConfig?.features?.courses !== false ? ' is-active' : ''}`}
                                        onClick={() => handleTabsFeatureToggle(bot.id, 'courses', tabsConfig?.features?.courses === false)}
                                        disabled={tabsBusy}
                                      >
                                        Kurs
                                      </button>
                                      <button
                                        type="button"
                                        className={`tabs-tool-feature-pill${tabsConfig?.features?.prices !== false ? ' is-active' : ''}`}
                                        onClick={() => handleTabsFeatureToggle(bot.id, 'prices', tabsConfig?.features?.prices === false)}
                                        disabled={tabsBusy}
                                      >
                                        Priser
                                      </button>
                                      <button
                                        type="button"
                                        className={`tabs-tool-feature-pill${tabsConfig?.features?.booking !== false ? ' is-active' : ''}`}
                                        onClick={() => handleTabsFeatureToggle(bot.id, 'booking', tabsConfig?.features?.booking === false)}
                                        disabled={tabsBusy}
                                      >
                                        Booking
                                      </button>
                                    </div>
                                    {isDrivingSchoolBot && (
                                      <div className="tabs-tool-config-field">
                                        <span>Standard kjøretime</span>
                                        <div className="tabs-tool-config-actions" style={{ marginTop: 8 }}>
                                          <button
                                            type="button"
                                            className="button button-secondary"
                                            onClick={() => handleTabsLessonOptionsLoad(bot.id)}
                                            disabled={tabsBusy}
                                          >
                                            {tabsLessonOptionsLoading ? 'Henter…' : 'Hent valg fra TABS'}
                                          </button>
                                        </div>
                                        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                                          <select
                                            className="input"
                                            value={selectedLessonCategoryId}
                                            onChange={(event) => handleTabsLessonCategoryChange(bot.id, event.target.value)}
                                            disabled={tabsBusy || lessonCategories.length === 0}
                                          >
                                            <option value="">Velg kategori</option>
                                            {lessonCategories.map((category) => (
                                              <option key={`${bot.id}-lesson-cat-${category.webCategoryId}`} value={String(category.webCategoryId)}>
                                                {category.categoryName || `Kategori ${category.webCategoryId}`}
                                              </option>
                                            ))}
                                          </select>
                                          <select
                                            className="input"
                                            value={selectedLessonProductId}
                                            onChange={(event) => handleTabsLessonProductChange(bot.id, event.target.value)}
                                            disabled={tabsBusy || !selectedLessonCategory || lessonProducts.length === 0}
                                          >
                                            <option value="">Velg produkt</option>
                                            {lessonProducts.map((product) => (
                                              <option key={`${bot.id}-lesson-product-${product.productId}`} value={String(product.productId)}>
                                                {product.productName || `Produkt ${product.productId}`}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <p className="input-hint" style={{ marginTop: 8 }}>
                                          Velges ingen standard, bruker boten automatisk Personbil/Kjøretime når mulig.
                                        </p>
                                      </div>
                                    )}
                                    {tabsUpdatedAt && (
                                      <p className="input-hint">Sist oppdatert: {tabsUpdatedAt}</p>
                                    )}
                                    {tabsLastTest && (
                                      <p className={`input-hint ${tabsLastTest.ok === true ? '' : 'error'}`}>
                                        {tabsTestModeLabel ? `Siste test (${tabsTestModeLabel}): ` : 'Siste test: '}
                                        {tabsLastTest.summary || 'Ingen oppsummering'}
                                      </p>
                                    )}
                                    {tabsError && (
                                      <p className="input-hint error">⚠️ {tabsError}</p>
                                    )}
                                    <div className="tabs-tool-config-actions">
                                      <button
                                        type="button"
                                        className="button button-primary"
                                        onClick={() => handleTabsConfigSave(bot.id)}
                                        disabled={tabsBusy}
                                      >
                                        {tabsSaving ? 'Lagrer…' : 'Lagre'}
                                      </button>
                                      <button
                                        type="button"
                                        className="button button-secondary"
                                        onClick={() => handleTabsConfigTest(bot.id)}
                                        disabled={tabsBusy}
                                      >
                                        {tabsTesting ? 'Tester…' : 'Test integrasjon'}
                                      </button>
                                      {tabsConfig?.configured === true && (
                                        <button
                                          type="button"
                                          className="button button-danger"
                                          onClick={() => handleTabsConfigDisconnect(bot.id)}
                                          disabled={tabsBusy}
                                        >
                                          Disconnect
                                        </button>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        );
      }
      case 'products': {
        const productItems = productItemsByBot[bot.id] || [];
        const productProposals = productProposalsByBot[bot.id] || [];
        const productSync = productSyncByBot[bot.id] || null;
        const isLoadingProducts = Boolean(productLoadingByBot[bot.id]);
        const isSavingProducts = Boolean(productSavingByBot[bot.id]);
        const productErr = productErrorByBot[bot.id] || null;
        const pendingProposals = productProposals.filter((entry) => entry.status === 'pending');
        const activeProducts = productItems.filter((entry) => entry.status !== 'archived');
        const archivedProducts = productItems.filter((entry) => entry.status === 'archived');
        const expandedProposalId = productProposalExpandedIdByBot[bot.id] || null;
        const productSyncMeta = productSync?.latestRun
          ? [
            `Siste sync ${formatDateTime(productSync.latestRun.finished_at || productSync.latestRun.updated_at)}`,
            productSync.latestRun.source_kind
              ? PRODUCT_SOURCE_LABELS[productSync.latestRun.source_kind] || productSync.latestRun.source_kind
              : null,
            productSync.latestRun.status || null,
          ].filter(Boolean).join(' · ')
          : null;

        if (!productItemsByBot[bot.id] && !productLoadingByBot[bot.id]) {
          loadProductsForBot(bot.id);
        }

        return (
          <section className="bot-section bot-section--products">
            <header className="bot-section-header">
              <div className="product-tab-heading">
                <h3>Produkt</h3>
                <p>Godkjenn produkter før boten bruker pris- og produktdata i svarene.</p>
              </div>
              <div className="product-tab-header-meta">
                {productSyncMeta && (
                  <p className="product-tab-sync">{productSyncMeta}</p>
                )}
                <div className="product-tab-header-controls">
                  <span className="product-tab-count">
                    {activeProducts.length} aktive produkter
                  </span>
                  <div className="bot-section-actions product-tab-header-actions">
                  <button
                    type="button"
                    className="button button-primary button-small"
                    onClick={() => openProductCreateModal(bot.id)}
                    disabled={!canEditBot || isSavingProducts}
                  >
                    Legg til produkt
                  </button>
                  <button
                    type="button"
                    className="button button-secondary button-small"
                    onClick={() => loadProductsForBot(bot.id, { force: true })}
                    disabled={isLoadingProducts}
                  >
                    Oppdater
                  </button>
                  </div>
                </div>
              </div>
            </header>

            {productErr && (
              <div className="bot-section-alert bot-section-alert--error">
                ⚠️ {productErr}
              </div>
            )}

            {isLoadingProducts ? (
              <div className="qa-empty">
                <p>Laster produkter…</p>
              </div>
            ) : activeProducts.length === 0 ? (
              <div className="qa-empty">
                <p>Ingen godkjente produkter ennå.</p>
              </div>
            ) : (
              <div className="qb-list qb-list--compact product-group-list product-group-list--primary">
                {activeProducts.map((item) => {
                  const configuredFields = Array.isArray(item.inboxConfig?.fields)
                    ? item.inboxConfig.fields.filter((field) => field?.key || field?.label)
                    : [];
                  const productMeta = [
                    formatCompactProductPrice({
                      priceDisplay: item.priceDisplay,
                      priceAmount: item.priceAmount,
                      priceCurrency: item.priceCurrency,
                    }),
                    configuredFields.length > 0
                      ? `${configuredFields.length} produktattributter`
                      : 'Ingen produktattributter',
                    item.updatedAt ? `Oppdatert ${formatDateTime(item.updatedAt)}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');

                  return (
                    <div key={item.id} className="qb-row qb-row--product">
                      <div className="qb-row-head">
                        <button
                          type="button"
                          className="qb-row-text qb-row-text-button"
                          onClick={() => openProductEditModal(bot.id, item)}
                          aria-label={`Rediger ${item.name || 'produkt'}`}
                        >
                          <div className="qb-title-row">
                            <div className="qb-title">{item.name || 'Uten navn'}</div>
                            <span className={`qb-source-badge ${item.sourceKind === 'website_scan' ? 'is-website' : 'is-manual'}`}>
                              {PRODUCT_SOURCE_LABELS[item.sourceKind] || item.sourceKind}
                            </span>
                          </div>
                          <div className="qb-snippet">{productMeta}</div>
                          {item.shortDescription && (
                            <div className="product-row-note">{item.shortDescription}</div>
                          )}
                        </button>
                        <div className="qb-actions qb-actions--compact">
                          <button
                            type="button"
                            className="button button-secondary button-small product-row-edit-button"
                            onClick={() => openProductEditModal(bot.id, item)}
                            disabled={!canEditBot || isSavingProducts}
                          >
                            Rediger
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="product-subsection">
              <div className="product-subsection-header">
                <div className="product-subsection-copy">
                  <h4 className="product-subsection-title">Forslag fra webscan/import</h4>
                  <p className="product-subsection-subtitle">
                    Gå raskt gjennom forslagene og åpne detaljer bare når du trenger mer kontekst.
                  </p>
                </div>
                <div className="product-subsection-actions">
                  {pendingProposals.length > 0 && (
                    <div className="product-primary-toolbar-actions">
                      <button
                        type="button"
                        className="button button-secondary button-small"
                        onClick={() => handleProductBulkAction(bot.id, 'approve')}
                        disabled={!canEditBot || isSavingProducts}
                      >
                        Godkjenn alle forslag
                      </button>
                      <button
                        type="button"
                        className="button button-secondary button-small"
                        onClick={() => handleProductBulkAction(bot.id, 'reject')}
                        disabled={!canEditBot || isSavingProducts}
                      >
                        Avvis alle forslag
                      </button>
                    </div>
                  )}
                  <span className="product-subsection-count">{pendingProposals.length} forslag</span>
                </div>
              </div>
              {pendingProposals.length === 0 ? (
                <div className="qa-empty">
                  <p>Ingen ventende produktforslag.</p>
                </div>
              ) : (
                <div className="qb-list qb-list--compact product-group-list">
                  {pendingProposals.map((proposal) => {
                    const expanded = expandedProposalId === proposal.id;
                    return (
                      <div key={proposal.id} className={`qb-row qb-row--product qb-row--proposal${expanded ? ' is-open' : ''}`}>
                        <div className="qb-row-head">
                          <button
                            type="button"
                            className="qb-row-text qb-row-text-button"
                            onClick={() => toggleProductProposalExpanded(bot.id, proposal.id)}
                            aria-expanded={expanded}
                          >
                            <div className="qb-title-row">
                              <div className="qb-title">{proposal.proposedName || 'Uten navn'}</div>
                              <span className="qb-source-badge is-website">
                                {PRODUCT_SOURCE_LABELS[proposal.sourceKind] || proposal.sourceKind}
                              </span>
                            </div>
                            <div className="qb-snippet">
                              {formatCompactProductPrice({
                                priceDisplay: proposal.proposedPriceDisplay,
                                priceAmount: proposal.proposedPriceAmount,
                                priceCurrency: proposal.proposedPriceCurrency,
                              })}
                              {proposal.confidence !== null && proposal.confidence !== undefined
                                ? ` · confidence ${Number(proposal.confidence).toFixed(2)}`
                                : ''}
                            </div>
                            {proposal.proposedShortDescription && (
                              <div className="product-row-note">{proposal.proposedShortDescription}</div>
                            )}
                          </button>
                          <div className="qb-actions qb-actions--compact">
                            <button
                              type="button"
                              className="button button-success button-small qb-icon-button"
                              onClick={() => handleProductProposalApprove(bot.id, proposal.id)}
                              disabled={!canEditBot || isSavingProducts}
                              aria-label="Godkjenn forslag"
                              title="Godkjenn forslag"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className="button button-danger button-small qb-icon-button"
                              onClick={() => handleProductProposalReject(bot.id, proposal.id)}
                              disabled={!canEditBot || isSavingProducts}
                              aria-label="Avvis forslag"
                              title="Avvis forslag"
                            >
                              ✕
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => toggleProductProposalExpanded(bot.id, proposal.id)}
                              disabled={!canEditBot}
                            >
                              {expanded ? 'Skjul' : 'Detaljer'}
                            </button>
                          </div>
                        </div>
                        {expanded && (
                          <div className="qb-row-body">
                            <p>{proposal.proposedShortDescription || 'Ingen beskrivelse oppgitt.'}</p>
                            <div className="bot-grid bot-grid--two">
                              <p className="input-hint">
                                Pris (beløp): {proposal.proposedPriceAmount ?? '—'}
                              </p>
                              <p className="input-hint">
                                Valuta: {proposal.proposedPriceCurrency || '—'}
                              </p>
                              <p className="input-hint" style={{ gridColumn: '1 / -1' }}>
                                Pris (tekst): {proposal.proposedPriceDisplay || '—'}
                              </p>
                            </div>
                            {proposal.proposedCategory && (
                              <p className="input-hint">Kategori: {proposal.proposedCategory}</p>
                            )}
                            {Array.isArray(proposal.proposedTags) && proposal.proposedTags.length > 0 && (
                              <p className="input-hint">Nøkkelord: {proposal.proposedTags.join(', ')}</p>
                            )}
                            {proposal.proposedUrl && (
                              <p className="input-hint">Kilde-URL: {proposal.proposedUrl}</p>
                            )}
                            {Array.isArray(proposal.evidence) && proposal.evidence.length > 0 && (
                              <p className="input-hint">Evidens: {proposal.evidence.slice(0, 3).join(' · ')}</p>
                            )}
                            <p className="input-hint">
                              Status: {PRODUCT_PROPOSAL_STATUS_LABELS[proposal.status] || proposal.status}
                              {proposal.updatedAt ? ` · Oppdatert ${formatDateTime(proposal.updatedAt)}` : ''}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {archivedProducts.length > 0 && (
              <div className="product-subsection">
                <div className="product-subsection-header">
                  <div className="product-subsection-copy">
                    <h4 className="product-subsection-title">Arkiverte produkter</h4>
                    <p className="product-subsection-subtitle">Hold arkivet kompakt, men lett å reaktivere ved behov.</p>
                  </div>
                  <span className="product-subsection-count">{archivedProducts.length} arkiverte</span>
                </div>
                <div className="qb-list qb-list--compact product-group-list">
                  {archivedProducts.map((item) => (
                    <div key={item.id} className="qb-row qb-row--product">
                      <div className="qb-row-head">
                        <div className="qb-row-text">
                          <div className="qb-title-row">
                            <div className="qb-title">{item.name || 'Uten navn'}</div>
                            <span className={`qb-source-badge ${item.sourceKind === 'website_scan' ? 'is-website' : 'is-manual'}`}>
                              {PRODUCT_SOURCE_LABELS[item.sourceKind] || item.sourceKind}
                            </span>
                          </div>
                          <div className="qb-snippet">
                            {[
                              formatCompactProductPrice({
                                priceDisplay: item.priceDisplay,
                                priceAmount: item.priceAmount,
                                priceCurrency: item.priceCurrency,
                              }),
                              item.updatedAt ? `Arkivert ${formatDateTime(item.updatedAt)}` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </div>
                        <div className="qb-actions qb-actions--compact">
                          <button
                            type="button"
                            className="button button-secondary button-small"
                            onClick={() => handleProductArchiveToggle(bot.id, item)}
                            disabled={!canEditBot || isSavingProducts}
                          >
                            Aktiver
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        );
      }
      case 'knowledge': {
        const botPairs = knowledgePairs[bot.id] || [];
        const manualPairs = botPairs.filter((pair) => pair.sourceType !== 'website');
        const websiteGroups = buildWebsiteKnowledgeGroups(botPairs);
        const websiteEntryCount = websiteGroups.reduce((sum, group) => sum + group.items.length, 0);
        const isLoadingKnowledge = knowledgeLoading[bot.id];
        const isSavingKnowledge = knowledgeSaving[bot.id];
        const knowledgeErr = knowledgeError[bot.id];
        const expandedPairId = knowledgeExpandedId[bot.id];
        const expandedWebsiteGroupId = knowledgeWebsiteExpandedId[bot.id];
        const generateModalOpen = knowledgeGenerateOpen[bot.id];
        const generateText = knowledgeGenerateText[bot.id] || '';
        const isGenerating = knowledgeGenerateLoading[bot.id];
        const maxEntries = knowledgeMaxEntries[bot.id];
        const hasMaxEntries = maxEntries !== null && maxEntries !== undefined;
        const atMaxEntries = hasMaxEntries && botPairs.length >= maxEntries;

        // Load knowledge when section is viewed
        if (!knowledgePairs[bot.id] && !knowledgeLoading[bot.id] && bot.externalId) {
          loadKnowledgeForBot(bot.id, bot.externalId);
        }

        return (
          <section className="bot-section">
            <header className="bot-section-header">
              <div>
                <h3>Kunnskapsbase</h3>
                <p>Administrer spørsmål og svar som boten kan bruke for å gi bedre svar.</p>
                <p className="input-hint">
                  Oppføringer: {botPairs.length}
                  {` (${manualPairs.length} manuelle / ${websiteEntryCount} webscan)`}
                  {hasMaxEntries ? ` / ${maxEntries}` : ''}
                  {hasMaxEntries && atMaxEntries ? ' (maks nådd)' : ''}
                </p>
              </div>
              <div className="bot-section-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => handleKnowledgeAddPair(bot.id)}
                  disabled={!canEditBot || atMaxEntries}
                  title={atMaxEntries ? `Maks ${maxEntries} oppføringer per bot for dette abonnementet.` : undefined}
                >
                  + Ny oppføring
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setKnowledgeGenerateOpen((prev) => ({ ...prev, [bot.id]: true }))}
                  disabled={!canEditBot || atMaxEntries}
                  title={atMaxEntries ? `Maks ${maxEntries} oppføringer per bot for dette abonnementet.` : undefined}
                >
                  Generer kunnskap
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => handleKnowledgeSave(bot.id, bot.externalId)}
                  disabled={isSavingKnowledge || !canEditBot}
                >
                  {isSavingKnowledge ? 'Lagrer…' : 'Lagre kunnskapsbase'}
                </button>
              </div>
            </header>

            {knowledgeErr && (
              <div className="bot-section-alert bot-section-alert--error">
                ⚠️ {knowledgeErr}
              </div>
            )}
            {!canEditBot && (
              <div className="bot-section-alert bot-section-alert--info">
                🔒 Du har lesevisning. Kontakt en eier eller manager dersom du trenger å redigere kunnskapsbasen.
              </div>
            )}

            {isLoadingKnowledge ? (
              <div className="qa-empty">
                <p>Laster kunnskapsbase…</p>
              </div>
            ) : botPairs.length === 0 ? (
              <div className="qa-empty">
                <p>Ingen kunnskapsinnslag. Klikk «+ Ny oppføring» for å starte.</p>
              </div>
            ) : (
              <div className="qb-list qb-list--compact">
                {manualPairs.length > 0 && (
                  <div className="qb-manual-section">
                    <p className="input-hint qb-section-label">Manuelle oppføringer</p>
                    {manualPairs.map((pair) => {
                      const expanded = expandedPairId === pair.id;
                      const snippet = (pair.content || '').slice(0, 140);
                      return (
                        <div key={pair.id} className={`qb-row${expanded ? ' is-open' : ''}`}>
                          <div className="qb-row-head">
                            <div
                              className="qb-row-text"
                              onClick={() =>
                                setKnowledgeExpandedId((prev) => ({
                                  ...prev,
                                  [bot.id]: expanded ? null : pair.id,
                                }))
                              }
                            >
                              <div className="qb-title-row">
                                <div className="qb-title">{pair.keyword || 'Uten tittel'}</div>
                                <span className="qb-source-badge is-manual">Manuell</span>
                              </div>
                              <div className="qb-snippet">
                                {snippet}
                                {snippet.length === 140 ? '…' : ''}
                              </div>
                            </div>
                            <div className="qb-actions">
                              <button
                                type="button"
                                className="button button-secondary"
                                onClick={() =>
                                  setKnowledgeExpandedId((prev) => ({
                                    ...prev,
                                    [bot.id]: expanded ? null : pair.id,
                                  }))
                                }
                              >
                                {expanded ? 'Lukk' : 'Utvid'}
                              </button>
                              <button
                                type="button"
                                className="button button-danger"
                                onClick={() => handleKnowledgeRemovePair(bot.id, pair.id)}
                                disabled={!canEditBot}
                              >
                                Slett
                              </button>
                            </div>
                          </div>
                          {expanded && (
                            <div className="qb-row-body">
                              <label>
                                Tittel eller kategori
                                <input
                                  className="input"
                                  type="text"
                                  value={pair.keyword}
                                  onChange={(event) =>
                                    handleKnowledgePairChange(bot.id, pair.id, 'keyword', event.target.value)
                                  }
                                  placeholder="Eks: Hvordan opprette bot"
                                  disabled={!canEditBot}
                                />
                              </label>
                              <label>
                                Kunnskap
                                <textarea
                                  className="textarea"
                                  rows={4}
                                  value={pair.content}
                                  onChange={(event) =>
                                    handleKnowledgePairChange(bot.id, pair.id, 'content', event.target.value)
                                  }
                                  placeholder="Instruksjon/kunnskap som skal brukes"
                                  disabled={!canEditBot}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {websiteGroups.length > 0 && (
                  <div className="qb-website-section">
                    <p className="input-hint qb-section-label">Webscan-kilder</p>
                    {websiteGroups.map((group) => {
                      const expanded = expandedWebsiteGroupId === group.id;
                      return (
                        <div key={group.id} className={`qb-website-group${expanded ? ' is-open' : ''}`}>
                          <button
                            type="button"
                            className="qb-website-group-head"
                            onClick={() =>
                              setKnowledgeWebsiteExpandedId((prev) => ({
                                ...prev,
                                [bot.id]: expanded ? null : group.id,
                              }))
                            }
                          >
                            <div className="qb-website-group-info">
                              <div className="qb-title-row">
                                <span className="qb-title">{group.label}</span>
                                <span className="qb-source-badge is-website">Webscan</span>
                              </div>
                              <span className="qb-website-group-meta">
                                {group.items.length} chunker
                                {group.updatedAt ? ` · Oppdatert ${formatDateTime(group.updatedAt)}` : ''}
                              </span>
                              {group.sourceUrl && (
                                <span className="qb-website-group-url">{group.sourceUrl}</span>
                              )}
                            </div>
                            <span className="qb-website-group-toggle">{expanded ? 'Lukk' : 'Vis'}</span>
                          </button>
                          {expanded && (
                            <div className="qb-website-group-body">
                              {group.items.map((item, index) => {
                                const chunkLabel = Number.isFinite(item.sourceChunkIndex)
                                  ? `Chunk ${item.sourceChunkIndex + 1}`
                                  : `Chunk ${index + 1}`;
                                return (
                                  <article key={item.id} className="qb-website-chunk">
                                    <div className="qb-website-chunk-head">
                                      <strong>{item.keyword || chunkLabel}</strong>
                                      <span className="qb-website-chunk-meta">
                                        {chunkLabel}
                                        {Number.isFinite(item.sourceQualityScore)
                                          ? ` · score ${item.sourceQualityScore.toFixed(2)}`
                                          : ''}
                                      </span>
                                    </div>
                                    <p>{item.content}</p>
                                  </article>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {generateModalOpen && (
              <div className="modal-overlay" role="dialog" aria-modal="true">
                <div className="modal" style={{ position: 'relative' }}>
                  {isGenerating && (
                    <div className="modal-loading-overlay">
                      <div className="thinking-animation">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </div>
                      <p className="loading-text">Analyserer teksten og genererer kunnskapsinnslag…</p>
                      <p className="loading-hint">Dette kan ta opptil ett minutt for lengre tekster.</p>
                    </div>
                  )}
                  <header className="modal-header">
                    <h2>Generer kunnskapsinnslag</h2>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => !isGenerating && setKnowledgeGenerateOpen((prev) => ({ ...prev, [bot.id]: false }))}
                      disabled={isGenerating}
                    >
                      ×
                    </button>
                  </header>
                  <div className="modal-body">
                    <p className="input-hint">
                      Lim inn produktbeskrivelser, interne rutiner eller andre kilder. Teksten bør være minst 50 tegn.
                    </p>
                    <textarea
                      className="textarea"
                      rows={8}
                      value={generateText}
                      onChange={(event) =>
                        setKnowledgeGenerateText((prev) => ({ ...prev, [bot.id]: event.target.value }))
                      }
                      placeholder="Beskriv produktet eller tjenesten..."
                      disabled={isGenerating}
                    />
                  </div>
                  <footer className="modal-footer">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => {
                        setKnowledgeGenerateOpen((prev) => ({ ...prev, [bot.id]: false }));
                        setKnowledgeGenerateText((prev) => ({ ...prev, [bot.id]: '' }));
                      }}
                      disabled={isGenerating}
                    >
                      Avbryt
                    </button>
                    <button
                      type="button"
                      className="button button-primary"
                      onClick={() => handleKnowledgeGenerate(bot.id, bot.externalId, generateText)}
                      disabled={isGenerating || generateText.trim().length < 50 || !canEditBot}
                    >
                      {isGenerating ? 'Genererer…' : 'Generer'}
                    </button>
                  </footer>
                </div>
              </div>
            )}
          </section>
        );
      }
      case 'schedules':
        // Render the new interactive schedule page component
        return <BotSchedulesPage bot={bot} />;
      case 'security':
        return (
          <section className="bot-section">
            <header className="bot-section-header">
              <h3>Sikkerhet</h3>
              <p>Administrer sikkerhet og tilgangskontroll for widgeten.</p>
            </header>
            <div className="security-section">
              <h4>Tillatte domener</h4>
              <p className="input-hint">
                Legg til domener som har lov til å bruke denne chat-widgeten. 
                Widgeten vil kun fungere på nettsider som matcher disse domenene.
                Støtter jokertegn: <code>*.example.com</code> matcher alle underdomener.
              </p>
              {(bot.allowedOrigins || []).length === 0 && (
                <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                  <strong>⚠️ Ingen domener konfigurert</strong>
                  <p>Du må legge til minst ett tillatt domene for at widgeten skal fungere.</p>
                </div>
              )}
              <div className="allowed-origins-list">
                {(bot.allowedOrigins || []).map((origin, index) => (
                  <div key={index} className="allowed-origin-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      className="input"
                      type="text"
                      value={origin}
                      onChange={(event) => handleAllowedOriginChange(bot.id, index, event.target.value)}
                      placeholder="https://example.com eller *.example.com"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="button button-secondary button-small"
                      onClick={() => handleAllowedOriginRemove(bot.id, index)}
                      title="Fjern domene"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => handleAllowedOriginAdd(bot.id)}
                style={{ marginTop: '0.5rem' }}
              >
                Legg til domene
              </button>
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <h5 style={{ margin: '0 0 0.5rem 0' }}>Eksempler på tillatte mønstre:</h5>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.9em' }}>
                  <li><code>https://example.com</code> - eksakt match</li>
                  <li><code>https://www.example.com</code> - med www</li>
                  <li><code>*.example.com</code> - alle underdomener (sub.example.com, app.example.com)</li>
                  <li><code>http://localhost:3000</code> - lokal utvikling (tillates automatisk i dev-modus)</li>
                </ul>
              </div>
            </div>
          </section>
        );
      case 'installation':
        const scriptTag = bot.externalId 
          ? DashboardConfig.getChatEmbedSnippet(bot.externalId)
          : '<script src="https://chat.kichat.no/embed.js" bot-id="din-bot-id"></script>';
        
        return (
          <section className="bot-section installation-section">
            <header className="bot-section-header">
              <h3>Installering</h3>
              <p>Legg til {bot.name || 'boten'} på nettstedet ditt med én enkel kodelinje.</p>
            </header>

            {/* Quick Actions Bar */}
            <div className="install-quick-actions">
              <button
                type="button"
                className={`install-action-btn install-action-copy ${installCopiedStep === 'quick' ? 'copied' : ''}`}
                onClick={() => handleCopyEmbed(bot, 'quick')}
                disabled={!bot.externalId}
              >
                <span className="install-action-icon">{installCopiedStep === 'quick' ? '✓' : '📋'}</span>
                <span className="install-action-text">
                  {installCopiedStep === 'quick' ? 'Kopiert!' : 'Kopier kode'}
                </span>
              </button>
              <button
                type="button"
                className="install-action-btn install-action-demo"
                onClick={() => handlePreview(bot)}
                disabled={!bot.externalId}
              >
                <span className="install-action-icon">🧪</span>
                <span className="install-action-text">Test i demo-miljø</span>
                <span className="install-action-badge">Nytt vindu</span>
              </button>
            </div>

            {/* Embed Code Display */}
            <div className="install-code-card">
              <div className="install-code-header">
                <span className="install-code-label">Script-kode for {bot.name || 'denne boten'}</span>
                <button
                  type="button"
                  className={`install-copy-btn ${installCopiedStep === 'code' ? 'copied' : ''}`}
                  onClick={() => handleCopyEmbed(bot, 'code')}
                  disabled={!bot.externalId}
                >
                  {installCopiedStep === 'code' ? '✓ Kopiert' : '📋 Kopier'}
                </button>
              </div>
              {bot.externalId ? (
                <code className="install-code-block">{scriptTag}</code>
              ) : (
                <p className="install-code-missing">
                  Embed-ID genereres automatisk når du lagrer boten.
                </p>
              )}
            </div>

            {/* HTML Placement Guide */}
            <div className="install-placement-card">
              <h4>Hvor skal koden plasseres?</h4>
              <p className="install-placement-desc">
                Lim inn script-koden rett før <code>&lt;/body&gt;</code>-taggen i HTML-filen din.
              </p>
              <div className="install-html-preview">
                <div className="html-line dimmed">&lt;html&gt;</div>
                <div className="html-line dimmed indent-1">&lt;head&gt;...&lt;/head&gt;</div>
                <div className="html-line dimmed indent-1">&lt;body&gt;</div>
                <div className="html-line dimmed indent-2">&lt;!-- Ditt innhold --&gt;</div>
                <div className="html-line highlighted indent-2">
                  <span className="placement-indicator">👆 Plasser her</span>
                  <code>{scriptTag}</code>
                </div>
                <div className="html-line dimmed indent-1">&lt;/body&gt;</div>
                <div className="html-line dimmed">&lt;/html&gt;</div>
              </div>
            </div>

            {/* Platform Guides */}
            <div className="install-platforms-card">
              <h4>Plattform-guider</h4>
              <p className="install-platforms-desc">Klikk på en plattform for å se detaljert installasjonsveiledning</p>
              <div className="install-platforms-grid">
                <button
                  type="button"
                  className="install-platform"
                  onClick={() => setPlatformGuideModal({
                    name: 'WordPress',
                    url: 'https://wordpress.com/support/code/',
                    icon: '🔷',
                    canEmbed: false
                  })}
                >
                  <span className="install-platform-icon">🔷</span>
                  <div className="install-platform-info">
                    <strong>WordPress</strong>
                    <span>Lim inn i <code>footer.php</code> eller bruk en plugin</span>
                  </div>
                  <span className="install-platform-arrow">→</span>
                </button>
                <button
                  type="button"
                  className="install-platform"
                  onClick={() => setPlatformGuideModal({
                    name: 'Webflow',
                    url: 'https://help.webflow.com/hc/en-us/articles/33961332238611-Custom-code-embed',
                    icon: '🌐',
                    canEmbed: false
                  })}
                >
                  <span className="install-platform-icon">🌐</span>
                  <div className="install-platform-info">
                    <strong>Webflow</strong>
                    <span>Project Settings → Custom Code → Footer</span>
                  </div>
                  <span className="install-platform-arrow">→</span>
                </button>
                <button
                  type="button"
                  className="install-platform"
                  onClick={() => setPlatformGuideModal({
                    name: 'Shopify',
                    url: 'https://help.shopify.com/en/manual/online-store/themes/customizing-themes/edit-code/edit-theme-code',
                    icon: '🛒',
                    canEmbed: true
                  })}
                >
                  <span className="install-platform-icon">🛒</span>
                  <div className="install-platform-info">
                    <strong>Shopify</strong>
                    <span>Themes → Edit code → theme.liquid</span>
                  </div>
                  <span className="install-platform-arrow">→</span>
                </button>
                <button
                  type="button"
                  className="install-platform"
                  onClick={() => setPlatformGuideModal({
                    name: 'Wix',
                    url: 'https://support.wix.com/en/article/wix-editor-embedding-custom-code-on-your-site',
                    icon: '🎨',
                    canEmbed: true
                  })}
                >
                  <span className="install-platform-icon">🎨</span>
                  <div className="install-platform-info">
                    <strong>Wix</strong>
                    <span>Settings → Custom Code → Body - End</span>
                  </div>
                  <span className="install-platform-arrow">→</span>
                </button>
              </div>
            </div>

          </section>
        );
      default:
        return null;
    }
  };

  const setupWizardBot = setupWizardBotId
    ? bots.find((bot) => bot.id === setupWizardBotId)
    : null;
  const setupWizardStep = BOT_WIZARD_STEPS[setupWizardStepIndex] || BOT_WIZARD_STEPS[0];
  const isSetupWizardLastStep = setupWizardStepIndex >= BOT_WIZARD_STEPS.length - 1;
  const productModalBot = productModalState?.botId
    ? bots.find((bot) => bot.id === productModalState.botId)
    : null;
  const productModalDraft = productModalState?.draft || createEmptyProductDraft();
  const productModalFields = Array.isArray(productModalDraft.inboxConfig?.fields)
    ? productModalDraft.inboxConfig.fields
    : [];
  const productModalIsSaving = productModalState?.botId
    ? Boolean(productSavingByBot[productModalState.botId])
    : false;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Bots</h1>
          <p className="page-subtitle">
            Administrer botene dine, juster adferd og design, og sett opp nye scenarier på få minutter.
          </p>
        </div>
        <div className="page-actions">
          <div className={`bot-limit ${botLimitReached ? 'is-max' : ''}`}>
            <span className="bot-limit-label">Bots</span>
            <strong>{botLimitLabel}</strong>
          </div>
          <button
            type="button"
            className="button button-primary"
            onClick={handleCreateBot}
            disabled={savingBotId === 'new' || botLimitReached}
            title={botLimitReached ? 'Maksgrense for bots er nådd' : 'Opprett ny bot'}
          >
            {savingBotId === 'new' ? 'Oppretter…' : '+ Ny bot'}
          </button>
        </div>
      </div>

      <article className="card">
        <header className="card-header">
          <div>
            <h2>Botoversikt</h2>
            <p className="card-subtitle">Filtrer og tilpass etter behov</p>
          </div>
          <input
            type="search"
            className="input"
            placeholder="Søk etter bot, segment eller status"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </header>

        {loading && (
          <div className="bot-empty">
            <p>Laster bots…</p>
          </div>
        )}

        <ul className="bot-list">
          {filteredBots.map((bot) => {
            const isExpanded = expandedBot === bot.id;
            const requestedSection = activeSections[bot.id];
            const activeSection = BOT_SECTIONS.some((section) => section.id === requestedSection)
              ? requestedSection
              : 'overview';
            const isActive = (bot.status || '').toLowerCase() !== 'deaktivert';
            const toggleLocked = isBillingBlocked;
            const toggleDisabled = toggleLocked || !canEditBot;
            const toggleLoading = Boolean(botToggleLoading[bot.id]);
            return (
              <li key={bot.id} className={`bot-row ${isExpanded ? 'expanded' : ''}`}>
                {!isExpanded && (
                <div className="bot-summary">
                  <div className="bot-color" style={{ backgroundColor: bot.color }} aria-hidden="true" />
                  <div className="bot-main">
                    <h3>{bot.name}</h3>
                    <p>
                      Segment: <strong>{bot.segment || 'Ikke satt'}</strong>
                    </p>
                  </div>
                  <div className="bot-status">
                    <button
                      type="button"
                      className={`bot-status-toggle ${isActive ? 'is-active' : 'is-inactive'}`}
                      onClick={() => handleBotToggle(bot)}
                      disabled={toggleDisabled || toggleLoading}
                      role="switch"
                      aria-checked={isActive}
                      title={toggleLocked ? 'Låst til betalingen er oppdatert' : 'Aktiver/deaktiver bot'}
                    >
                      <span className="bot-status-track">
                        <span className="bot-status-thumb" />
                      </span>
                      <span className="bot-status-text">{isActive ? 'Aktiv' : 'Deaktivert'}</span>
                    </button>
                    {toggleLocked && (
                      <span className="bot-status-lock">Låst</span>
                    )}
                  </div>
                  <div className="bot-meta">
                    <span className="meta-label">
                      {bot.updated
                        ? `Oppdatert ${new Date(bot.updated).toLocaleString('no-NO')}`
                        : 'Ingen endringer'}
                    </span>
                    <button
                      type="button"
                        className="bot-toggle-btn"
                      onClick={() => toggleExpanded(bot.id)}
                      aria-expanded={expandedBot === bot.id}
                      aria-controls={`bot-settings-${bot.id}`}
                    >
                        <span className="bot-toggle-icon">⚙</span>
                        <span>Innstillinger</span>
                    </button>
                  </div>
                </div>
                )}

                {isExpanded && (
                  <header className="bot-expanded-header">
                    <div className="bot-expanded-header-top">
                      <div className="bot-expanded-title">
                        <div className="bot-color" style={{ backgroundColor: bot.color }} aria-hidden="true" />
                        <h3>{bot.name}</h3>
                        <span className="bot-segment">Segment: {bot.segment || 'Ikke satt'}</span>
                      </div>
                      <div className="bot-expanded-meta">
                        <div className={`bot-status bot-status--inline ${toggleLocked ? 'is-locked' : ''}`}>
                          <button
                            type="button"
                            className={`bot-status-toggle ${isActive ? 'is-active' : 'is-inactive'}`}
                            onClick={() => handleBotToggle(bot)}
                            disabled={toggleDisabled || toggleLoading}
                            role="switch"
                            aria-checked={isActive}
                            title={toggleLocked ? 'Låst til betalingen er oppdatert' : 'Aktiver/deaktiver bot'}
                          >
                            <span className="bot-status-track">
                              <span className="bot-status-thumb" />
                            </span>
                            <span className="bot-status-text">{isActive ? 'Aktiv' : 'Deaktivert'}</span>
                          </button>
                          {toggleLocked && (
                            <span className="bot-status-lock">Låst</span>
                          )}
                        </div>
                        <span>
                          {bot.updated
                            ? `Oppdatert ${new Date(bot.updated).toLocaleString('no-NO')}`
                            : 'Ingen endringer'}
                        </span>
                        <button
                          type="button"
                          className="bot-close-btn"
                          onClick={() => toggleExpanded(bot.id)}
                          aria-expanded={true}
                          aria-controls={`bot-settings-${bot.id}`}
                          title="Lukk innstillinger"
                        >
                          <span>✕</span>
                        </button>
                      </div>
                    </div>
                  <nav className="bot-detail-nav" aria-label={`Innstillinger for ${bot.name}`}>
                    {BOT_SECTIONS.map((section) => (
                      <button
                        type="button"
                        key={section.id}
                        className={`bot-nav-button ${activeSection === section.id ? 'is-active' : ''}`}
                        onClick={() =>
                          setActiveSections((prev) => ({
                            ...prev,
                            [bot.id]: section.id,
                          }))
                        }
                      >
                        {section.label}
                      </button>
                    ))}
                  </nav>
                  </header>
                )}

                {isExpanded && (
                  <div id={`bot-settings-${bot.id}`} className="bot-settings">
                    <div className="bot-detail-layout">
                      <div className="bot-detail-content">
                        {renderBotSection(activeSection, bot)}
                        <div className="bot-actions">
                          <button
                            type="button"
                            className="button button-primary"
                            onClick={() => handleSave(bot)}
                            disabled={savingBotId === bot.id}
                          >
                            {savingBotId === bot.id ? 'Lagrer…' : 'Lagre endringer'}
                          </button>
                          {activeSection === 'overview' ? (
                            <button
                              type="button"
                              className="button button-danger"
                              onClick={() => handleDelete(bot)}
                              disabled={savingBotId === bot.id}
                            >
                              {savingBotId === bot.id ? 'Sletter…' : 'Slett bot'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            );
          })}

          {!loading && filteredBots.length === 0 && (
            <li className="bot-empty">
              <p>Ingen bots samsvarer med søket. Prøv et annet stikkord.</p>
            </li>
          )}
        </ul>
      </article>

      {productModalState && createPortal(
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={productModalState.mode === 'create' ? 'Legg til produkt' : 'Rediger produkt'}
          onClick={() => !productModalIsSaving && closeProductModal()}
        >
          <div
            className="modal modal--lg product-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="modal-header">
              <div className="modal-title">
                <div>
                  <h2>{productModalState.mode === 'create' ? 'Legg til produkt' : 'Rediger produkt'}</h2>
                  <p className="input-hint">
                    {productModalBot?.name
                      ? `${productModalBot.name} · Produktdetaljer og produktspesifikke attributter`
                      : 'Produktdetaljer og produktspesifikke attributter'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={closeProductModal}
                disabled={productModalIsSaving}
                aria-label="Lukk produktdialog"
              >
                ✕
              </button>
            </header>

            <div className="modal-body product-modal-body">
              <section className="product-modal-panel">
                <div className="product-modal-panel-header">
                  <div>
                    <h4>Produktdetaljer</h4>
                    <p className="input-hint">Hold oppsettet kort og tydelig. Resten av tabben forblir kompakt.</p>
                  </div>
                </div>
                <div className="bot-grid bot-grid--two">
                  <label>
                    Navn
                    <input
                      className="input"
                      type="text"
                      value={productModalDraft.name}
                      onChange={(event) => handleProductModalFieldChange('name', event.target.value)}
                      placeholder="F.eks. Klasse B vanlig opplæring"
                      disabled={!canEditBot || productModalIsSaving}
                      autoFocus
                    />
                  </label>
                  <label>
                    Pris (beløp)
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={productModalDraft.priceAmount ?? ''}
                      onChange={(event) => handleProductModalFieldChange('priceAmount', event.target.value)}
                      placeholder="19990"
                      disabled={!canEditBot || productModalIsSaving}
                    />
                  </label>
                  <label>
                    Valuta
                    <input
                      className="input"
                      type="text"
                      value={productModalDraft.priceCurrency || ''}
                      onChange={(event) => handleProductModalFieldChange('priceCurrency', event.target.value.toUpperCase())}
                      placeholder="NOK"
                      disabled={!canEditBot || productModalIsSaving}
                    />
                  </label>
                  <label>
                    Pris (tekst)
                    <input
                      className="input"
                      type="text"
                      value={productModalDraft.priceDisplay || ''}
                      onChange={(event) => handleProductModalFieldChange('priceDisplay', event.target.value)}
                      placeholder="Fra 19 990 kr"
                      disabled={!canEditBot || productModalIsSaving}
                    />
                  </label>
                  <label style={{ gridColumn: '1 / -1' }}>
                    Kort beskrivelse
                    <textarea
                      className="textarea"
                      rows={3}
                      value={productModalDraft.shortDescription || ''}
                      onChange={(event) => handleProductModalFieldChange('shortDescription', event.target.value)}
                      placeholder="Kort beskrivelse av produktet eller tjenesten"
                      disabled={!canEditBot || productModalIsSaving}
                    />
                  </label>
                </div>
              </section>

              <section className="product-modal-panel">
                <div className="product-modal-panel-header">
                  <div>
                    <h4>Varsling</h4>
                    <p className="input-hint">Send e-post når en ny henvendelse registreres med dette produktet.</p>
                  </div>
                </div>
                <div className="product-notification-settings">
                  <label className="checkbox-field">
                    <input
                      type="checkbox"
                      checked={productModalDraft.notificationSettings?.enabled === true}
                      onChange={(event) => handleProductModalNotificationChange('enabled', event.target.checked)}
                      disabled={!canEditBot || productModalIsSaving}
                    />
                    <span>Aktiver e-postvarsling for dette produktet</span>
                  </label>
                  {productModalDraft.notificationSettings?.enabled === true ? (
                    <label>
                      E-postmottaker
                      <input
                        className="input"
                        type="email"
                        value={productModalDraft.notificationSettings?.emailDestination || ''}
                        onChange={(event) => handleProductModalNotificationChange('emailDestination', event.target.value)}
                        placeholder="support@example.no"
                        disabled={!canEditBot || productModalIsSaving}
                      />
                    </label>
                  ) : null}
                </div>
              </section>

              <section className="product-modal-panel product-modal-panel--config">
                <div className="product-inbox-config-header">
                  <div>
                    <h5>Produktspesifikke attributter</h5>
                    <p className="input-hint">
                      Disse attributtene aktiveres når en henvendelse velger dette produktet, og kan samles inn i chat eller fylles ut internt i inboxen.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="button button-secondary button-small"
                    onClick={handleProductModalInboxConfigAddField}
                    disabled={!canEditBot || productModalIsSaving}
                  >
                    + Felt
                  </button>
                </div>

                {productModalFields.length === 0 ? (
                  <div className="product-inbox-config-empty">
                    <p>Ingen produktspesifikke attributter konfigurert ennå.</p>
                  </div>
                ) : (
                  <div className="product-inbox-config-list">
                    {productModalFields.map((field, fieldIndex) => (
                      <div key={`modal-inbox-field-${fieldIndex}`} className="product-inbox-config-row">
                        <div className="bot-grid bot-grid--two">
                          <label>
                            Nøkkel
                            <input
                              className="input"
                              type="text"
                              value={field.key}
                              onChange={(event) =>
                                handleProductModalInboxConfigFieldChange(fieldIndex, 'key', event.target.value)}
                              disabled={!canEditBot || productModalIsSaving}
                              placeholder="f.eks. kommune"
                            />
                          </label>
                          <label>
                            Etikett
                            <input
                              className="input"
                              type="text"
                              value={field.label}
                              onChange={(event) =>
                                handleProductModalInboxConfigFieldChange(fieldIndex, 'label', event.target.value)}
                              disabled={!canEditBot || productModalIsSaving}
                              placeholder="f.eks. Kommune"
                            />
                          </label>
                          <label>
                            Felttype
                            <select
                              className="select"
                              value={field.type}
                              onChange={(event) =>
                                handleProductModalInboxConfigFieldChange(fieldIndex, 'type', event.target.value)}
                              disabled={!canEditBot || productModalIsSaving}
                            >
                              {PRODUCT_INBOX_FIELD_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Standardverdi
                            {field.type === 'boolean' ? (
                              <select
                                className="select"
                                value={String(field.defaultValue ?? '')}
                                onChange={(event) =>
                                  handleProductModalInboxConfigFieldChange(fieldIndex, 'defaultValue', event.target.value)}
                                disabled={!canEditBot || productModalIsSaving}
                              >
                                <option value="">Ingen</option>
                                <option value="true">Ja</option>
                                <option value="false">Nei</option>
                              </select>
                            ) : (
                              <input
                                className="input"
                                type={field.type === 'integer' ? 'number' : 'text'}
                                value={field.defaultValue ?? ''}
                                onChange={(event) =>
                                  handleProductModalInboxConfigFieldChange(fieldIndex, 'defaultValue', event.target.value)}
                                disabled={!canEditBot || productModalIsSaving}
                                placeholder="Valgfri standardverdi"
                              />
                            )}
                          </label>
                          <label style={{ gridColumn: '1 / -1' }}>
                            Hjelpetekst
                            <input
                              className="input"
                              type="text"
                              value={field.helpText}
                              onChange={(event) =>
                                handleProductModalInboxConfigFieldChange(fieldIndex, 'helpText', event.target.value)}
                              disabled={!canEditBot || productModalIsSaving}
                              placeholder="Kort forklaring under feltet"
                            />
                          </label>
                          <label style={{ gridColumn: '1 / -1' }}>
                            Placeholder
                            <input
                              className="input"
                              type="text"
                              value={field.placeholder}
                              onChange={(event) =>
                                handleProductModalInboxConfigFieldChange(fieldIndex, 'placeholder', event.target.value)}
                              disabled={!canEditBot || productModalIsSaving}
                              placeholder="Tekst som vises i tomt felt"
                            />
                          </label>
                          {field.type === 'select' ? (
                            <div className="label" style={{ gridColumn: '1 / -1' }}>
                              <span className="label-text">Valg</span>
                              <SelectOptionsEditor
                                options={field.options}
                                onChange={(nextOptions) =>
                                  handleProductModalInboxConfigFieldChange(fieldIndex, 'options', nextOptions)}
                                disabled={!canEditBot || productModalIsSaving}
                              />
                            </div>
                          ) : null}
                        </div>
                        <div className="product-inbox-config-row-actions">
                          <div className="inquiry-attribute-flags">
                            <label className="checkbox-field">
                              <input
                                type="checkbox"
                                checked={field.collectInChat === true}
                                onChange={(event) =>
                                  handleProductModalInboxConfigFieldChange(fieldIndex, 'collectInChat', event.target.checked)}
                                disabled={!canEditBot || productModalIsSaving}
                              />
                              <span>Samle i chat når produktet er valgt</span>
                            </label>
                            <label className="checkbox-field">
                              <input
                                type="checkbox"
                                checked={field.required === true}
                                onChange={(event) =>
                                  handleProductModalInboxConfigFieldChange(fieldIndex, 'required', event.target.checked)}
                                disabled={!canEditBot || productModalIsSaving || field.collectInChat !== true}
                              />
                              <span>Påkrevd når den samles inn</span>
                            </label>
                            <label className="checkbox-field">
                              <input
                                type="checkbox"
                                checked={field.showInInboxList === true}
                                onChange={(event) =>
                                  handleProductModalInboxConfigFieldChange(fieldIndex, 'showInInboxList', event.target.checked)}
                                disabled={!canEditBot || productModalIsSaving}
                              />
                              <span>Vis som kolonnevalg i henvendelser</span>
                            </label>
                          </div>
                          <div className="qb-actions qb-actions--compact">
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => handleProductModalInboxConfigMoveField(fieldIndex, 'up')}
                              disabled={!canEditBot || productModalIsSaving || fieldIndex === 0}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => handleProductModalInboxConfigMoveField(fieldIndex, 'down')}
                              disabled={!canEditBot || productModalIsSaving || fieldIndex === productModalFields.length - 1}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              className="button button-danger button-small"
                              onClick={() => handleProductModalInboxConfigRemoveField(fieldIndex)}
                              disabled={!canEditBot || productModalIsSaving}
                            >
                              Fjern
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <footer className="modal-footer modal-footer--between">
              <div>
                {productModalState.mode === 'edit' && (
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={handleProductModalArchive}
                    disabled={!canEditBot || productModalIsSaving}
                  >
                    Arkiver
                  </button>
                )}
              </div>
              <div className="product-modal-footer-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={closeProductModal}
                  disabled={productModalIsSaving}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleProductModalSave}
                  disabled={!canEditBot || productModalIsSaving}
                >
                  {productModalIsSaving ? 'Lagrer…' : 'Lagre'}
                </button>
              </div>
            </footer>
          </div>
        </div>,
        document.body,
      )}

      {setupWizardOpen && createPortal(
        <div className="setup-wizard-overlay" role="dialog" aria-modal="true" aria-label="Oppsettveiviser">
          <div className="setup-wizard" onClick={(e) => e.stopPropagation()}>
            <header className="setup-wizard-header">
              <div className="setup-wizard-title">
                <h2>Oppsettveiviser</h2>
                <p>
                  {setupWizardBot?.name
                    ? `Konfigurer ${setupWizardBot.name} steg-for-steg.`
                    : 'Konfigurer boten steg-for-steg.'}
                </p>
              </div>
              <button
                type="button"
                className="setup-wizard-close"
                onClick={() => closeSetupWizard({ expandAfterClose: true })}
                aria-label="Lukk oppsettveiviser"
              >
                ✕
              </button>
            </header>

            <nav className="setup-wizard-steps" aria-label="Oppsettsteg">
              {BOT_WIZARD_STEPS.map((step, index) => {
                const isActiveStep = index === setupWizardStepIndex;
                const isDone = index < setupWizardStepIndex;
                return (
                  <button
                    key={step.id}
                    type="button"
                    className={`setup-wizard-step ${isActiveStep ? 'is-active' : ''} ${isDone ? 'is-done' : ''}`}
                    onClick={() => setSetupWizardStepIndex(index)}
                  >
                    <span className="setup-wizard-step-index">{index + 1}</span>
                    <span className="setup-wizard-step-label">{step.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="setup-wizard-body">
              <div className="setup-wizard-step-meta">
                <div className="setup-wizard-step-heading">
                  <h3>{setupWizardStep?.label}</h3>
                  {setupWizardStep?.description && <p>{setupWizardStep.description}</p>}
                </div>
                <div className="setup-wizard-step-progress">
                  Steg {setupWizardStepIndex + 1} av {BOT_WIZARD_STEPS.length}
                </div>
              </div>

              {!setupWizardBot ? (
                <div className="setup-wizard-loading">
                  <p>Laster bot…</p>
                </div>
              ) : (
                <div className="setup-wizard-content">
                  {renderBotSection(setupWizardStep.sectionId, setupWizardBot)}
                </div>
              )}
            </div>

            <footer className="setup-wizard-footer">
              <button
                type="button"
                className="button button-secondary"
                onClick={handleSetupWizardBack}
                disabled={setupWizardStepIndex === 0}
              >
                Tilbake
              </button>

              <div className="setup-wizard-footer-right">
                {!isSetupWizardLastStep ? (
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={handleSetupWizardNext}
                    disabled={!setupWizardBot || savingBotId === setupWizardBotId}
                  >
                    {savingBotId === setupWizardBotId ? 'Lagrer…' : 'Neste'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={handleSetupWizardFinish}
                    disabled={!setupWizardBot || savingBotId === setupWizardBotId}
                  >
                    {savingBotId === setupWizardBotId ? 'Lagrer…' : 'Fullfør'}
                  </button>
                )}
              </div>
            </footer>
          </div>
        </div>,
        document.body,
      )}

      {/* Platform Guide Modal - Rendered via Portal to document.body for proper z-index layering */}
      {platformGuideModal && createPortal(
        <div className="platform-guide-modal-overlay" onClick={() => {
          setPlatformGuideModal(null);
          setPlatformIframeLoaded(false);
          setPlatformIframeFailed(false);
        }}>
          <div 
            className={`platform-guide-modal ${platformGuideModal.canEmbed && platformIframeLoaded && !platformIframeFailed ? 'platform-guide-modal-embed' : 'platform-guide-modal-compact'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="platform-guide-modal-header">
              <div className="platform-guide-modal-title">
                <span className="platform-guide-modal-icon">{platformGuideModal.icon}</span>
                <h3>{platformGuideModal.name} - Installasjonsveiledning</h3>
              </div>
              <button
                type="button"
                className="platform-guide-modal-close"
                onClick={() => {
                  setPlatformGuideModal(null);
                  setPlatformIframeLoaded(false);
                  setPlatformIframeFailed(false);
                }}
              >
                ✕
              </button>
            </div>
            <div className="platform-guide-modal-help">
              <span className="platform-guide-modal-help-icon">💬</span>
              <span>Hvis du trenger hjelp, ikke nøl med å kontakte oss i chatten!</span>
            </div>
            
            {/* Try to embed the page if platform supports it */}
            {platformGuideModal.canEmbed && !platformIframeFailed && (
              <div className={`platform-guide-iframe-container ${platformIframeLoaded ? 'is-loaded' : ''}`}>
                {!platformIframeLoaded && (
                  <div className="platform-guide-loading">
                    <div className="platform-guide-spinner"></div>
                    <span>Laster inn {platformGuideModal.name} veiledning...</span>
                  </div>
                )}
                <iframe
                  src={platformGuideModal.url}
                  title={`${platformGuideModal.name} installation guide`}
                  className="platform-guide-iframe"
                  onLoad={() => {
                    setPlatformIframeLoaded(true);
                  }}
                  onError={() => {
                    setPlatformIframeFailed(true);
                  }}
                />
              </div>
            )}

            {/* Fallback instructions for platforms that don't support embedding or if iframe fails */}
            {(!platformGuideModal.canEmbed || platformIframeFailed) && (
              <div className="platform-guide-modal-body">
                <div className="platform-guide-info">
                  <div className="platform-guide-info-icon">📖</div>
                  <h4>Offisiell {platformGuideModal.name} dokumentasjon</h4>
                  <p>
                    Klikk på knappen under for å åpne den offisielle veiledningen fra {platformGuideModal.name}. 
                    Der finner du steg-for-steg instruksjoner for hvordan du legger til vår chat-widget.
                  </p>
                  <div className="platform-guide-steps">
                    <div className="platform-guide-step">
                      <span className="platform-guide-step-num">1</span>
                      <span>Åpne veiledningen i et nytt vindu</span>
                    </div>
                    <div className="platform-guide-step">
                      <span className="platform-guide-step-num">2</span>
                      <span>Følg instruksjonene for å legge til egendefinert kode</span>
                    </div>
                    <div className="platform-guide-step">
                      <span className="platform-guide-step-num">3</span>
                      <span>Lim inn scriptet du kopierte fra installeringssiden</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="platform-guide-modal-footer">
              <button
                type="button"
                className="button button-secondary"
                onClick={() => {
                  setPlatformGuideModal(null);
                  setPlatformIframeLoaded(false);
                  setPlatformIframeFailed(false);
                }}
              >
                Lukk
              </button>
              <a
                href={platformGuideModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="button button-primary"
              >
                Åpne i nytt vindu ↗
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BotsPage;
